import type { Dict } from './index.js';

export const en: Dict = {
  'app.name': 'RPG Desk',
  'app.tagline': 'Everything you need to play.',

  'login.welcome': 'Good to see you again!',
  'login.account': 'Account or e-mail',
  'login.password': 'Password',
  'login.showPassword': 'Show password',
  'login.hidePassword': 'Hide password',
  'login.keepSignedIn': 'Keep me signed in',
  'login.submit': 'Sign in',
  'login.forgotPassword': 'Forgot password',
  'login.register': "Don't have an account? Create one now",
  'login.error.accountRequired': 'Enter your account or e-mail',
  'login.error.passwordRequired': 'Enter your password',
  'login.error.failed': 'Could not sign in',
  'login.connectionProblem': 'Connection problem',

  'register.cancel': 'I already have an account: back to sign in',
  'register.welcome': "Let's create your account",
  'register.email': 'E-mail',
  'register.account': 'Account name',
  'register.gender': 'How should we refer to you?',
  'register.gender.masculine': 'Masculine (he)',
  'register.gender.feminine': 'Feminine (she)',
  'register.gender.neutral': 'Neutral',
  'register.continue': 'Continue',
  'register.step2.welcome': 'Choose a password',
  'register.password': 'Password',
  'register.confirmPassword': 'Repeat the password',
  'register.wantPromoMail': 'Send me news by e-mail',
  'register.acceptTos': 'I am {age} or older and accept the {terms} and the {privacy}',
  'register.submit': 'Create account',
  'register.back': 'Back',

  'register.error.required': 'Required field',
  'register.error.tooShort': 'Too short (at least 3 characters)',
  'register.error.tooLong': 'Too long (at most 24 characters)',
  'register.error.invalid.displayName': 'Use letters, digits, space, dot, hyphen or underscore',
  'register.error.invalid.email': 'Invalid e-mail',
  'register.error.taken.displayName': 'This name is already taken',
  'register.error.taken.email': 'This e-mail is already registered',
  'register.error.gender': 'Pick an option',
  'register.error.passwordEmpty': 'Choose a password',
  'register.error.passwordMismatch': "Passwords don't match",
  'register.error.tosRequired': 'Confirm your age and accept the terms to continue',
  'register.error.failed': 'Could not create the account',

  'forgot.title': 'Recover password',
  'forgot.text': 'E-mail recovery is not available on this server yet. Ask the administrator to reset your password.',

  // the e-mail confirmation (docs/spec/email.md): the waiting screen and the link
  'confirmEmail.title': 'Confirm your e-mail',
  'confirmEmail.sent': 'We sent a link to {email}.',
  'confirmEmail.open': 'Open the e-mail and click Confirm e-mail. The link is valid for {hours} hours.',
  'confirmEmail.failed': 'We could not send the e-mail to {email}. Try sending it again.',
  'confirmEmail.resend': 'Resend e-mail',
  'confirmEmail.resendIn': 'Resend in {time}',
  'confirmEmail.resent': 'We sent another link. Check your spam folder too.',
  'confirmEmail.wrong': 'Wrong e-mail?',
  'confirmEmail.deleteAt': 'Without the confirmation, the account is deleted on {date}.',
  'confirmEmail.change.text': 'Type the right e-mail. The link goes to it.',
  'confirmEmail.change.submit': 'Send to this e-mail',
  'confirmEmail.change.cancel': 'Back',
  'confirmEmail.logout': 'Sign out',
  'confirmEmail.link.confirmed.title': 'E-mail confirmed',
  'confirmEmail.link.confirmed': 'All set. Sign in to your account to start.',
  'confirmEmail.link.expired.title': 'Link expired',
  'confirmEmail.link.expired': 'This confirmation link has expired. Sign in to your account and ask for a new one.',
  'confirmEmail.link.invalid.title': 'Link no longer valid',
  'confirmEmail.link.invalid': 'This confirmation link is no longer valid: it was already used, or a newer one was sent. Sign in to your account to continue.',
  'confirmEmail.link.failed.title': 'Could not confirm',
  'confirmEmail.link.failed': 'We could not confirm the e-mail right now. Check your connection and open the link again.',
  'confirmEmail.toast.confirmed': 'E-mail confirmed. Welcome to RPG Desk!',
  'confirmEmail.toast.expired': 'This confirmation link has expired. Ask for a new one.',
  'confirmEmail.toast.invalid': 'This confirmation link is no longer valid.',
  'confirmEmail.toast.failed': 'We could not confirm the e-mail right now. Open the link again.',

  'legal.terms.title': 'Terms of Use',
  'legal.privacy.title': 'Privacy Policy',
  'legal.terms.short': 'Terms of use',
  'legal.privacy.short': 'Privacy',
  'legal.nav': 'Terms and privacy',
  'legal.toc': 'On this page',
  'legal.meta': 'Version {version} · Updated {date}',
  'legal.loading': 'Loading…',
  'legal.error': "Couldn't open the text. Check your connection and try again.",
  'legal.back': 'Back to RPG Desk',
  'legal.update.title': 'Our terms have changed',
  'legal.update.text':
    "RPG Desk's Terms of Use and Privacy Policy were updated on {date}. To keep using your account, read and accept the new version.",
  'legal.update.accept': 'Accept and continue',
  'legal.update.signOut': 'Sign out',

  'logged.nav.label': 'Main navigation',
  'logged.nav.dashboard': 'Home',
  'logged.nav.rooms': 'Rooms',
  'logged.nav.friends': 'Friends',
  'logged.nav.cloud': 'Desk Cloud',
  'logged.nav.settings': 'Settings',
  'logged.nav.logout': 'Sign out',
  'logged.nav.more': 'More',
  'logged.nav.openRooms': 'Open rooms',
  'logged.nav.room': 'Room',
  'logged.rooms.leave': 'Leave room',
  'logged.rooms.back': 'Back to the room {name}',
  'logged.rooms.tooMany': '{max} rooms are already open: leave one (right-click its button) to open another',
  'logged.rooms.unread': '{count} unread',
  'logged.rooms.mention': 'mention',
  'logged.rooms.clock.running': 'session {n} running, {played} of play',
  'logged.rooms.clock.paused': 'session {n} on a break, {played} of play',

  'room.entering': 'Entering the room…',
  'room.people': '{n} in the room',
  'room.status.online': 'Connected',
  'room.status.connecting': 'Connecting…',
  'room.status.offline': 'Disconnected',
  'room.voice': 'Voice and video',
  'room.voice.inCall': 'In the call (voice and video)',
  'room.voice.on': 'In the call',
  'room.leave': 'Leave room',
  'room.closeModule': 'Close',
  // the room on a phone (docs/spec/mobile.md)
  'room.mobile.back': 'Back',
  'room.mobile.tabs': 'Room tabs',
  'room.mobile.tab.chat': 'Chat',
  'room.mobile.tab.scene': 'Scene',
  'room.mobile.tab.sheets': 'Sheets',
  'room.mobile.tab.people': 'People',
  'room.mobile.tab.more': 'More',
  'room.mobile.newChat': 'New conversation',
  'room.mobile.chatWith': 'Chat with {name}',
  'room.mobile.openOther': 'Open another',
  'room.mobile.scenes.pick': 'Pick a scene',
  'room.mobile.scenes.empty': 'The room has no scenes you can see yet.',
  'room.mobile.sheets.pick': 'Pick a sheet',
  'room.mobile.sheets.empty': 'The room has no sheets you can see yet.',

  'module.mainChat': 'Chat',
  'module.userList': 'Participants',
  'module.library': 'Library',
  'module.scene': 'Scene',
  'module.music': 'Music',
  'module.sfx': 'Sound effects',
  'module.files': 'Files',
  'module.gameSessions': 'Sessions',
  'module.tableDock': 'Table panel',
  'module.roomSettings': 'Room settings',

  'chat.tools': 'Chat tools',
  'chat.sendButton': 'Send',
  'chat.tool.format': 'Formatting and speak as',
  'chat.tool.bold': 'Bold',
  'chat.tool.italic': 'Italic',
  'chat.tool.underline': 'Underline',
  'chat.tool.color': 'Text color',
  'chat.tool.dice': 'Roll 1d{n}',
  'chat.persona': 'Speak as',
  'chat.persona.none': 'Myself',
  'chat.persona.narrator': 'Narrator',
  'chat.persona.npc': 'NPC…',
  'chat.persona.npcName': 'NPC name',
  'chat.input.prompt': 'Type a message…',
  'chat.input.send': 'Send',
  'chat.rolled': 'rolled',
  'chat.rolling': 'rolling…',

  'dice.failure': 'Failure',
  'dice.criticalFailure': 'Critical failure',
  'dice.oneSuccess': '1 success',
  'dice.nSuccesses': '{n} successes',
  'dice.dropped': 'Left out of the sum',
  'dice.error.empty': 'The roll is empty.',
  'dice.error.unknownChar': 'The roll has a character that is not allowed: "{detail}".',
  'dice.error.missingFace': 'The die is missing its number of faces (d20, d8, d6…).',
  'dice.error.unknownRoll': 'That dice roll does not exist (an extra "d"?).',
  'dice.error.unknownOperator': 'Unknown operator in the roll.',
  'dice.error.unknownComparator': 'Unknown comparator: "{detail}". Use > to count successes.',
  'dice.error.maxOneComparator': 'A roll can only have one comparator (>).',
  'dice.error.minOneDie': 'The roll needs at least one die.',
  'dice.error.flood': 'Roll too large: up to 300 dice and numbers.',
  'dice.error.bigNumber': 'Number too large in the roll: up to 1,000,000.',
  'dice.error.tooLong': 'The roll is longer than {max} characters, counting the text after the ":".',
  'error.noSession': 'not signed in',
  'error.badImage': 'invalid image',
  'error.imageSlow': 'the image took too long to load',
  'error.imageLoad': 'could not load the image',
  'scene.uvtt.notJson': 'the file is not JSON',
  'scene.uvtt.noSize': 'the file has no valid map size (resolution.map_size)',
  'scene.uvtt.noImage': 'the file does not include the map image',
  'scene.uvtt.badImage': 'the map image is not PNG, WebP or JPEG',
  'api.unreachable': 'couldn\'t reach the server (is it running?)',
  'api.unexpected': 'unexpected error',
  'api.status': 'error {status}',
  'api.uploadFailed': 'upload failed (HTTP {status})',
  'api.uploadCanceled': 'upload canceled',
  'api.noAnswer': 'the server did not answer',
  'dice.error.timeout': 'The roll did not come back from the server.',

  'users.group.gms': 'Game master',
  'users.group.players': 'Player',
  'users.online': 'Online',
  'users.you': 'you',

  'room.menu': 'Room menu',
  'room.panels.out': 'It is in its own window: click to bring it to the front.',
  'room.tabs': 'Tabs',
  'room.tabs.hint': 'Open and close the room tabs and arrange the screen',
  'room.panelMenu': 'More space actions',
  'room.closePanel': 'Close the space',
  'room.panel.collapse': 'Collapse',
  'room.panel.maximize': 'Maximize',
  'room.panel.restore': 'Restore',
  'room.panel.expand': 'Expand',
  'room.panel.empty': 'Open a scene or sheet from the library, or drag a tab here.',
  'room.panel.empty.window': 'Open a tab here with the +, or bring one from the room (right-click the tab → Move to).',
  'room.panel.emptyLabel': 'Empty space',
  'room.panel.new': 'New space beside it',
  'room.tab.add': 'Open a tab here',
  'room.tab.add.elsewhere': 'In another space: moves here',
  'room.tab.add.chatCopy': 'The room chat stays there, and a copy comes here',
  'room.tab.moveTo': 'Move to',
  'room.tab.moveInto': 'With {name}',
  'room.group.chats.fixed': "The room chat doesn't close.",
  'room.move': 'Move to',
  'room.move.left': 'Left',
  'room.move.center': 'Center',
  'room.move.right': 'Right',
  'room.move.table': 'Move to the room',
  'room.where.here': 'In this window',
  'room.where.table': 'In the room',
  'room.where.otherWindow': 'In another window',
  'room.window.new': 'New window',
  'room.leave.confirm.title': 'Leave the room?',
  'room.leave.confirm.message': 'You leave {name}. To come back, join it again from Home or Rooms.',
  'room.layout.edit': 'Arrange the screen',
  'room.layout.done': 'Done arranging',
  'room.layout.banner': 'Arranging the screen: drag a tab, or a whole space by the empty part of its tab strip, to the edge of another space or of the table. Holding Ctrl or Alt when you drop leaves a copy of the tab. The + puts a tab in that spot. Ctrl+E or Esc leaves.',
  'room.layout.banner.window': 'Arranging the window: drag a tab, or a whole space by the empty part of its tab strip, to the edge of another space or of the window. Holding Ctrl or Alt when you drop leaves a copy of the tab. The + puts a tab in that spot. Ctrl+E or Esc leaves.',
  'room.layout.banner.narrow': 'In a narrow window all the tabs stay together. Widen the window to arrange the screen.',
  'room.layout.reset': 'Restore the starting layout',
  'room.layout.reset.message': "This room's tabs go back to the starting arrangement: the library on the left, an empty space and the chat in the middle, and the participants on the right.",
  'room.layout.reset.action': 'Restore the starting layout',
  'room.layout.clone': 'Duplicate',
  'room.layout.dragHint': 'Drag to move the whole space, with its tabs',
  'room.layout.add.title': 'Put here',
  'room.layout.add.left': 'Put a tab to the left of this space',
  'room.layout.add.right': 'Put a tab to the right of this space',
  'room.layout.add.top': 'Put a tab above this space',
  'room.layout.add.bottom': 'Put a tab below this space',
  'room.layout.add.center': 'Put a tab in the center',
  'room.layout.edge.left': 'New column on the left of the table',
  'room.layout.edge.right': 'New column on the right of the table',
  'room.layout.edge.top': 'New strip at the top of the table',
  'room.layout.edge.bottom': 'New strip at the bottom of the table',
  'room.layout.edge.window.left': 'Split the window: a tab on the left',
  'room.layout.edge.window.right': 'Split the window: a tab on the right',
  'room.layout.edge.window.top': 'Split the window: a tab on top',
  'room.layout.edge.window.bottom': 'Split the window: a tab at the bottom',
  'room.layout.emptyCenter': 'The center of the table is empty: drag a tab, or a space, here.',
  'room.layout.emptyCenter.editing': 'Drag a tab or a space here, or use the +.',
  'chat.joined': 'joined',
  'chat.left': 'left',
  'chat.tool.diceBar': 'Quick dice',
  'library.search': 'Search the library',
  'library.newFolder': 'New folder',
  'library.folder': 'Folder',
  'library.empty': 'The library is empty.',

  'users.group.gm.one': 'Game master',
  'users.group.gm.many': 'Game masters',
  'users.group.player.one': 'Player',
  'users.group.player.many': 'Players',
  'users.group.spectator.one': 'Spectator',
  'users.group.spectator.many': 'Spectators',
  'users.menu': 'Actions for {name}',
  'users.menu.profile': 'View profile',
  'users.menu.photo': 'View photo',
  'users.menu.toGm': 'Make game master',
  'users.menu.toPlayer': 'Make player',
  'users.menu.toSpectator': 'Make spectator',
  'users.menu.mute': 'Silence',
  'users.menu.unmute': 'Allow to talk',
  'users.menu.blind': 'Hide the chat from them',
  'users.menu.unblind': 'Show the chat again',
  'users.menu.voice': 'May talk in moderated chat',
  'users.menu.unvoice': 'May not talk in moderated chat',
  'users.menu.kick': 'Kick',
  'users.menu.ban': 'Ban',
  'users.menu.ban.confirm': 'Ban {name}? The account won\'t be able to come back to this room until unbanned.',
  'users.menu.nick': 'Change my nickname',
  'users.menu.nick.prompt': 'Nickname in this room (empty goes back to your account name)',
  'users.mode.mute': 'silenced',
  'users.mode.blind': 'not seeing chat',
  'users.mode.voice': 'has voice',
  'users.typing': 'typing…',
  'mode.gm': 'game master',
  'mode.player': 'player',
  'mode.mute': 'silence',
  'mode.blind': 'blindness',
  'mode.voice': 'voice',
  'mode.juggernaut': 'juggernaut',
  'chat.event.modes.added': 'gave {modes} to {target}',
  'chat.event.modes.removed': 'took {modes} from {target}',
  'chat.event.nick': 'is now called {to}',
  'chat.event.kick': 'kicked {target}',
  'chat.event.ban': 'banned {target}',
  'chat.event.moderated.on': 'turned moderated chat on',
  'chat.event.moderated.off': 'turned moderated chat off',
  'chat.typing.one': '{a} is typing…',
  'chat.typing.two': '{a} and {b} are typing…',
  'chat.typing.many': 'Several people are typing…',
  'chat.moderatedBanner': 'Chat is moderated: only game masters and people with voice can talk.',
  'chat.moderated.end': 'End',
  'chat.delete.label': "Delete {name}'s message",
  'chat.delete.roll.label': "Delete {name}'s roll",
  'chat.delete.hint': 'Delete (Shift+click deletes without asking)',
  'chat.delete.title': "Delete {name}'s message?",
  'chat.delete.roll.title': "Delete {name}'s roll?",
  'chat.delete.message': "It disappears from everyone's chat. Game masters can still see it with /audit.",
  'chat.delete.action': 'Delete',
  'chat.delete.offline': 'No connection: nothing was deleted.',
  'chat.delete.menu': 'Delete message…',
  'chat.delete.roll.menu': 'Delete roll…',
  'chat.line.message': "{name}'s message",
  'chat.line.roll': "{name}'s roll",
  'chat.line.copy': 'Copy the text',
  'chat.line.copied': 'Text copied.',
  'chat.line.copyFailed': "Couldn't copy the text.",
  'chat.clear': 'Clear the chat…',
  'chat.clear.title': 'Clear the room chat?',
  'chat.clear.message': "Every message, roll and system line disappears from everyone's chat. Game masters can still see them with /audit. Private conversations don't change.",
  'chat.clear.action': 'Clear',
  'chat.audit.banner': 'Audit mode: deleted messages show up marked.',
  'chat.audit.exit': 'Close',
  'chat.audit.exit.label': 'Close audit mode',
  'chat.deleted.mark': '(deleted)',
  'chat.deleted.by': 'Deleted by {name} on {when}',
  'chat.deleted.byClear': 'Deleted by {name} when clearing the chat, on {when}',
  'room.moderate.on': 'Moderate chat',
  'room.moderate.off': 'End chat moderation',
  'room.removed.kicked': '{by} removed you from the room.',
  'room.removed.banned': '{by} banned you from this room.',

  'explore.nav': 'Show',
  'explore.tab.online': 'Playing now',
  'explore.only.new': 'Only new to me',
  'explore.newRoom': 'New room',
  'explore.empty.hint.recent': 'Rooms you enter show up here.',
  'explore.empty.hint.favorites': 'Right-click a room to keep it in your favorites.',
  'explore.empty.hint.mine': 'Create a room to start running games.',
  'explore.sort': 'Sort: {mode}',
  'explore.sort.recommendation': 'recommended',
  'explore.sort.onlinePlayersQty': 'most people online',
  'explore.loading': 'Loading…',
  'explore.search.words': 'Name, system, game master, status or description',
  'explore.search.label': 'Search rooms',
  'explore.facets': 'Filters',
  'explore.facets.status': 'Status',
  'explore.facets.system': 'System',
  'explore.facets.noSystems': 'No room here names its system.',
  'explore.facets.allSystems': 'Show all ({n})',
  'explore.facets.fewerSystems': 'Show fewer',
  'explore.facets.tagsHint': 'One tap shows only rooms with the trait; a second, only rooms without it; a third removes the filter.',
  'explore.facets.clear': 'Clear filters',
  'explore.facets.language': 'Language',
  'explore.facets.noLanguages': 'No room here names its language.',
  'explore.facets.frequency': 'Frequency',
  'explore.facets.visuals': 'Visuals',
  'explore.facets.pay': 'Cost',
  'explore.facets.pay.any': 'Any',
  'explore.facets.pay.free': 'Free',
  'explore.facets.pay.paid': 'Paid',
  'explore.tag.any': 'any',
  'explore.tag.in': 'only with it',
  'explore.tag.out': 'only without it',
  'explore.tag.without': 'without {name}',
  'explore.count.one': '1 room',
  'explore.count.many': '{n} rooms',
  'explore.sortLabel': 'Sort',
  'explore.layout': 'View',
  'explore.layout.grid': 'Grid',
  'explore.layout.list': 'List',
  'explore.card.open': 'See details of {name}',
  'explore.empty.filtered': 'No rooms match these filters',
  'explore.empty.filtered.text': 'Remove a filter, pick another system or see all rooms.',
  'explore.empty.online': 'Nobody is playing right now',
  'explore.empty.online.text': 'When someone joins a public room, it shows up here.',
  'explore.empty.discover': 'You have been to every public room',
  'explore.empty.discover.text': 'New rooms show up here.',
  'explore.empty.search': 'No public rooms yet',
  'explore.empty.search.text': 'Create the first one: it shows up here for whoever is looking.',
  'explore.empty.all': 'See all rooms',
  'explore.empty.create': 'Create a room',
  'explore.search.withoutPassword': 'No password',
  'explore.search.withGMOnline': 'GM in the room',
  'explore.card.gmOnline': 'GM in the room',
  'explore.card.gmOffline': 'GM away',
  'explore.card.password': 'Password',
  'explore.card.mine': 'Your room',
  'explore.card.auxGm': 'You are GM',
  'explore.card.vacancies': 'Looking for players',
  'explore.card.adult': '18+',
  'explore.card.gm': 'GM',
  'explore.card.gmHere': 'in the room',
  'explore.card.nobody': 'Nobody now',
  'explore.card.playing.one': '1 in the room',
  'explore.card.playing.many': '{n} in the room',
  'explore.filters.show': 'Show {count}',
  'explore.filters.more': 'More filters',
  'explore.card.menu': 'Actions for room {name}',
  'explore.menu.like': 'Like this room',
  'explore.menu.unlike': 'Undo like',
  'explore.menu.dislike': 'Not for me',
  'explore.menu.undislike': 'Undo "not for me"',
  'explore.menu.favorite': 'Keep in favorites',
  'explore.menu.unfavorite': 'Remove from favorites',
  'explore.menu.overview': 'Details',
  'explore.menu.copyLink': 'Copy room link',
  'explore.menu.copied': 'Link copied.',
  'explore.overview.onlineNow': 'In the room now',
  'explore.overview.created': 'Created',
  'explore.overview.visited': 'Your last visit: {when}',
  'explore.overview.notVisited': "You haven't been in this room yet",
  'explore.overview.description': 'Description',
  'explore.overview.about': 'About this room',
  'explore.overview.details': 'What the room is like',
  'explore.overview.password': 'Room password',
  'explore.overview.join': 'Enter room',
  'explore.overview.rejoin': 'Back to the room',
  'explore.overview.likes.one': '1 like',
  'explore.overview.likes.many': '{n} likes',
  'explore.overview.dislikes.one': '1 not for them',
  'explore.overview.dislikes.many': '{n} not for them',
  'create.title': 'New room',
  'create.name': 'Room name',
  'create.name.prompt': 'Something eye-catching',
  'create.name.tooShort': 'Use at least {n} characters.',
  'create.system': 'System',
  'create.system.prompt': 'D&D 5e, Call of Cthulhu, Vampire…',
  'create.complement': 'Details',
  'create.complement.prompt': 'Edition, setting, supplement…',
  'create.tags': 'Traits',
  'create.password.toggle': 'Require a password',
  'create.password': 'Password',
  'create.welcome': 'Welcome message',
  'create.welcome.prompt': 'Shown in chat to whoever enters',
  'create.private': 'Hide from Explore',
  'create.submit': 'Create and enter',
  'create.cancel': 'Cancel',
  'roomDetails.none': 'Not set',
  'roomDetails.language': 'Language',
  'roomDetails.frequency': 'Frequency',
  'roomDetails.frequency.several-weekly': 'Several times a week',
  'roomDetails.frequency.weekly': 'Weekly',
  'roomDetails.frequency.biweekly': 'Every other week',
  'roomDetails.frequency.monthly': 'Monthly',
  'roomDetails.frequency.irregular': 'Irregular',
  'roomDetails.frequency.one-shot': 'Single session',
  'roomDetails.visuals': 'Visuals',
  'roomDetails.visuals.maps': 'Maps and tokens',
  'roomDetails.visuals.some-images': 'Some images',
  'roomDetails.visuals.theater-of-mind': 'Theater of the mind',
  'roomDetails.payToPlay': 'I charge to run games',
  'roomDetails.payToPlay.desc': 'Shows the Paid badge on the room card. People looking for a room can filter for free or paid rooms only.',
  'roomDetails.price': 'Price',
  'roomDetails.price.prompt': 'E.g. $5 per session',
  'roomDetails.free': 'Free',
  'roomDetails.paid': 'Paid',
  'roomDetails.paidWithPrice': 'Paid · {price}',
  'roomDetails.about': 'About this room',
  'roomDetails.about.prompt': 'What sessions are like, what to expect, what players need to have…',
  'home.greeting.morning': 'Good morning, {name}',
  'home.greeting.afternoon': 'Good afternoon, {name}',
  'home.greeting.evening': 'Good evening, {name}',
  'home.tagline': 'The table is set. So are the dice.',
  'home.search': 'Search rooms, systems or game masters',
  'home.create': 'Create room',
  'home.hero.enter': 'Join the table',
  'home.hero.back': 'Back to the table',
  'home.hero.invite': 'Invite players',
  'home.hero.invited': 'Room link copied. Send it to your players.',
  'home.hero.gm': 'Game master present',
  'home.hero.seen.now': 'You were here just now',
  'home.hero.seen.minutes': 'You were here {n} min ago',
  'home.hero.seen.hours': 'You were here {n} h ago',
  'home.hero.seen.yesterday': 'You were here yesterday',
  'home.hero.seen.days': 'You were here {n} days ago',
  'home.hero.seen.unknown': '',
  'home.online.none': 'Nobody at the table right now',
  'home.online.one': '1 person at the table now',
  'home.online.many': '{n} people at the table now',
  'home.visit.now': 'Last visit just now',
  'home.visit.minutes': 'Last visit {n} min ago',
  'home.visit.hours': 'Last visit {n} h ago',
  'home.visit.yesterday': 'Last visit yesterday',
  'home.visit.days': 'Last visit {n} days ago',
  'home.visit.unknown': 'Not visited yet',
  'home.rooms.title': 'Your tables',
  'home.rooms.seeAll': 'See all rooms',
  'home.rooms.filter': 'Filter tables',
  'home.rooms.all': 'Show all ({n})',
  'home.group.recent': 'Recent',
  'home.group.favorites': 'Favorites',
  'home.group.mine': 'Mine',
  'home.card.enter': 'Join {name}',
  'home.newRoom.title': 'Create a new table',
  'home.newRoom.text': 'Set up the room, invite your group and roll initiative.',
  'home.empty.recent': 'No recent tables',
  'home.empty.favorites': 'No favorite tables yet',
  'home.empty.favorites.text': 'Open a room’s ⋮ menu and choose “Keep in favorites” to pin it here.',
  'home.empty.mine': 'You haven’t created a table yet',
  'home.explore.title': 'Looking for a new table?',
  'home.explore.text': 'Find open rooms for the system you play and talk straight to the game master.',
  'home.explore.systems': 'Most played systems',
  'home.explore.go': 'Explore rooms',
  'home.explore.short': 'Explore',
  'roomTag.campaign': 'Campaign',
  'roomTag.one-shot': 'One-shot',
  'roomTag.beginners': 'Beginner friendly',
  'roomTag.veterans': 'For veterans',
  'roomTag.fantasy': 'Fantasy',
  'roomTag.horror': 'Horror',
  'roomTag.sci-fi': 'Science fiction',
  'roomTag.modern': 'Modern day',
  'roomTag.historical': 'Historical',
  'roomTag.investigation': 'Investigation',
  'roomTag.comedy': 'Comedy',
  'roomTag.intrigue': 'Intrigue',
  'roomTag.combat': 'Combat heavy',
  'roomTag.roleplay': 'Roleplay heavy',
  'roomTag.voice': 'Uses voice',
  'roomTag.text-only': 'Text only',
  'roomTag.homebrew': 'House rules',
  'roomTag.mature': 'Mature themes',

  'cmd.unknown': 'I don\'t know the command {cmd}. Type {help} for the list.',
  'cmd.failed': 'The command /{cmd} didn\'t work.',
  'cmd.help.heading': 'Chat commands',
  'cmd.help.formatting': '**Formatting:** _italic_, **bold**, *action*, "speech", --em dash speech--, ((out of character)), {I}nitial cap, [§K4]color[§O]',
  'cmd.help.title': '/help',
  'cmd.help.desc': 'shows this list.',
  'cmd.roll.title': '/roll or /r <dice>',
  'cmd.roll.desc': 'rolls dice: /r 2d6+5 · /r 4d10 > 7 (successes against 7) · /r ord 4d6 (sorted) · /r 4d6a3 (the 3 highest) · /r 2d20b (the lowest) · /r 4dF · /r 1d20+5: Attack (with a message).',
  'cmd.roll.empty': 'Say what to roll, e.g. /r 1d20+3.',
  'cmd.me.title': '/me <action>',
  'cmd.me.desc': 'narrates an action of your character.',
  'cmd.me.empty': 'Write the action after /me.',
  'cmd.laugh.title': '/lol',
  'cmd.laugh.desc': 'laughs in the chat.',
  'cmd.laugh.default': 'hahaha',
  'cmd.cls.title': '/cls',
  'cmd.cls.desc': 'hides what was already in your chat (only for you).',
  'cmd.fls.title': '/fls',
  'cmd.fls.desc': 'shows again what /cls hid.',
  'cmd.prink.title': '/prink',
  'cmd.prink.desc': 'hides system messages (joins, leaves…) from your chat.',
  'cmd.reload.title': '/reload',
  'cmd.reload.desc': 'undoes /prink and /cls.',
  'cmd.audit.title': '/audit',
  'cmd.audit.desc': 'GMs only: shows the deleted messages in the room chat, marked; again, hides them.',
  'cmd.audit.gmOnly': 'Only game masters use /audit, in the room chat.',
  'cmd.part.title': '/leave <message>',
  'cmd.part.desc': 'leaves the room with a farewell.',
  'cmd.nick.title': '/nick <nickname>',
  'cmd.nick.desc': 'changes how you show up in this room; without a nickname, goes back to your account name.',
  'cmd.fm.title': '/fm <feet>',
  'cmd.fm.desc': 'converts feet to meters, only for you: the rulebook way (5 ft = 1.5 m) and the exact one.',
  'cmd.fm.empty': 'Say how many feet, e.g. /fm 40.',
  'cmd.mf.title': '/mf <meters>',
  'cmd.mf.desc': 'converts meters to feet, only for you: the rulebook way (1.5 m = 5 ft) and the exact one.',
  'cmd.mf.empty': 'Say how many meters, e.g. /mf 60.',
  'cmd.length.result': '{from} = {to} (exact: {exact})',
  'cmd.length.same': '{from} = {to}',
  'room.removed.deleted': 'The room was deleted by {by}.',
  'room.inviteFriends': 'Invite friends',
  'room.inviteFriends.menu': 'Invite friends…',
  'room.talkToGm': 'Talk to the GM',
  'room.settings': 'Room settings',
  'room.settings.plugins': 'Room plugins',
  'settings.nav': 'Settings sections',
  'settings.tab.profile': 'Profile',
  'settings.tab.permissions': 'Permissions',
  'settings.tab.system': 'System',
  'settings.save': 'Save',
  'settings.saved': 'Settings saved.',
  'settings.discard': 'Undo',
  'settings.profile.name': 'Room name',
  'settings.profile.status': 'Status message',
  'settings.profile.status.prompt': 'e.g. session 12 this Saturday',
  'settings.profile.cover': 'Cover image',
  'settings.profile.logo': 'Room badge',
  'settings.profile.image.pick': 'Choose image…',
  'settings.profile.image.remove': 'Remove',
  'settings.profile.image.broken': 'The image does not open: the file left the Desk Cloud or the address does not answer.',
  'settings.profile.cover.hint': 'At the top of the room card, on Home and Explore.',
  'settings.profile.logo.hint': 'In the circle over the cover.',
  'settings.profile.vacancies': 'Looking for players',
  'settings.profile.adult': 'Content for adults (18+)',
  'settings.profile.private': 'Hide from Explore',
  'settings.permissions.password': 'Password to enter',
  'settings.permissions.password.has': 'The room has a password.',
  'settings.permissions.password.none': 'The room is open.',
  'settings.permissions.password.new': 'New password',
  'settings.permissions.password.set': 'Set password',
  'settings.permissions.password.remove': 'Remove password',
  'settings.permissions.log': 'Who reads the chat history',
  'settings.permissions.log.everyone': 'Everyone sees the whole history',
  'settings.permissions.log.receipt': 'Each person only sees what happened since they first joined',
  'settings.system.auxGms': 'Assistant game masters',
  'settings.system.auxGms.hint': 'They join as game masters, skip the password and can change settings.',
  'settings.system.bans': 'Banned',
  'settings.system.bans.hint': 'They can\'t enter this room.',
  'settings.system.add': 'Add',
  'settings.system.account': 'Account name',
  'settings.system.remove': 'Remove',
  'settings.system.empty': 'Nobody on the list.',
  'settings.system.delete': 'Delete room',
  'settings.system.delete.hint': 'Deletes the room, chat and rolls forever. Owner only.',
  'settings.system.delete.confirm': 'Delete "{name}" forever? Type the room name to confirm.',
  'settings.ownerOnly': 'Only the owner and assistant game masters change settings.',
  'settings.sub.profile': 'How the room looks in Explore and to whoever joins.',
  'settings.sub.game': "The bars and lines on the players' cards, and the session records.",
  'settings.sub.plugins': 'The sheets and tables the room uses.',
  'settings.sub.permissions': 'Who gets in and what each person reads in the chat.',
  'settings.sub.system': 'Assistant game masters, bans and deleting the room.',
  'settings.scope': 'Applies to the whole room',
  'settings.scope.tip': 'Saved on the server: everyone in the room sees the change.',
  'settings.unsaved': 'You have changes that are not saved yet.',
  'settings.card.presentation': 'Presentation',
  'settings.card.presentation.desc': "The room's name, system and messages.",
  'settings.card.tags.desc': 'Up to 12, for the Explore search.',
  'settings.card.images': 'Images',
  'settings.card.images.desc': 'From your Desk Cloud, your computer or a web address. Without one, RPG Desk draws a cover and shows the initials.',
  'settings.card.explore': 'In Explore',
  'settings.card.details': 'What the room is like',
  'settings.card.details.desc': 'Shown in the room overview, and used as filters by people looking for a room in Explore.',
  'settings.card.about.desc': 'The text of the room overview, for people who find it in Explore. Chat formatting works here.',
  'settings.card.password': 'Entry',
  'settings.card.chat': 'Chat',
  'settings.profile.vacancies.desc': 'Shows the Looking for players badge on the room card.',
  'settings.profile.adult.desc': 'Shows the 18+ badge. The room disappears from Explore for people who hide 18+ rooms.',
  'settings.profile.private.desc': 'The room can only be found through its link.',
  'settings.permissions.log.everyone.short': 'Everything',
  'settings.permissions.log.receipt.short': 'Since joining',
  'chat.event.room.name': 'renamed the room to {value}',
  'chat.event.room.welcome': 'changed the welcome message',
  'chat.event.room.welcome.removed': 'removed the welcome message',
  'chat.event.room.status': 'set the room status to {value}',
  'chat.event.room.status.removed': 'removed the room status',
  'chat.event.room.tags': 'set the room traits to {value}',
  'chat.event.room.tags.removed': 'removed the room traits',
  'chat.event.room.password': 'changed the room password',
  'chat.event.room.password.removed': 'removed the room password',
  'chat.event.room.gameRules': 'changed the game rules (card bars and lines)',
  'module.pvtChat': 'Private chat',
  'module.pvtChat.titleFmt': '{name} (private)',
  'module.groupPvtChat': 'Group chat',
  'module.groupPvtChat.new': 'Private group chat…',
  'pvt.away': 'This person left the room; they won\'t get messages until they come back.',
  'pvt.group.gone': 'You are no longer in this chat.',
  'pvt.group.away': '(not in the room)',
  'pvt.group.invite': 'Invite more',
  'pvt.group.leave': 'Leave chat',
  'pvt.group.invite.title': 'Invite to the chat',
  'pvt.group.invite.action': 'Invite',
  'pvt.group.invite.empty': 'Everyone in the room is already here.',
  'pvt.group.new.title': 'New private group chat',
  'pvt.group.new.action': 'Create chat',
  'pvt.group.new.empty': 'Nobody else is in the room right now.',
  'users.menu.dm': 'Chat',
  'users.menu.pvt': 'Talk privately',
  'users.menu.groupPvt': 'Start a group chat',
  'users.newGroup': 'Group chat',
  'chat.event.groupCreated': 'started the group chat',
  'chat.event.groupInvited': 'brought {target} into the chat',
  'chat.event.groupParted': 'left the chat',
  'chat.event.chatCleared': 'cleared the chat',
  // session clock (docs/spec/session-clock.md): the chat lines and the pill
  'chat.event.session.start': 'Session {n} started {when}',
  'chat.event.session.pause': 'Break {when}',
  'chat.event.session.pause.auto': 'Automatic break {when} · no game master in the room',
  'chat.event.session.resume': 'Back {when} · {pause} break',
  'chat.event.session.end': 'Session {n} ended {when} · {played} of play',
  'chat.event.session.end.paused': ' · {paused} of breaks',
  'chat.event.session.by': 'by {name}',
  'clock.label': 'Session clock',
  'clock.start': 'Start session {n}',
  'clock.pause': 'Pause the session',
  'clock.resume': 'Resume the session',
  'clock.end': 'End the session',
  'clock.end.menu': 'End the session…',
  'clock.end.ask': 'End now?',
  'clock.end.yes': 'End',
  'clock.end.no': 'Keep playing',
  'clock.end.confirm': 'End session {n}, with {played} of play?',
  'clock.menu': 'Session clock: pause or end',
  'clock.paused': 'break',
  'clock.ended': 'Session {n} · {played}',
  'clock.rate': 'Rate',
  'clock.rate.session': 'Rate the session',
  'clock.rate.players': 'Rate the players',
  'clock.timer': 'Session {n}: {played} of play',
  'clock.timer.paused': 'Session {n} on a break: {played} of play',
  'clock.details': 'Session {n} · started at {time}',
  'clock.details.pauses.one': ' · 1 break, {paused}',
  'clock.details.pauses.many': ' · {count} breaks, {paused}',
  'clock.duration.under': 'under 1 min',
  'clock.duration.minutes': '{m} min',
  'clock.duration.hours': '{h}h',
  'clock.duration.hoursMinutes': '{h}h{mm}',
  'clock.at': 'at {time}',
  'clock.atDay': 'on {day} at {time}',
  // the Sessions tab (docs/spec/session-clock.md): the open session and the record of the earlier ones
  'sessions.unavailable': "This server doesn't have the session clock yet.",
  'sessions.off': 'Session records are off in this room.',
  'sessions.none': 'No session running.',
  'sessions.empty': "This is where each game session is recorded: how long it lasted, the breaks, who was there and the game master's notes.",
  'sessions.eyebrow': 'Session {n} · {day}',
  'sessions.status.running': 'In play',
  'sessions.status.paused': 'On a break',
  'sessions.startedAt': 'Started at {time}',
  'sessions.timeline': 'Session timeline, {range}',
  'sessions.key.play': 'play',
  'sessions.key.pause': 'break',
  'sessions.people': 'In the session',
  'sessions.people.past': 'Who was there',
  'sessions.role.gm': 'game master',
  'sessions.person.since': 'since {time}',
  'sessions.notes': 'Notes',
  'sessions.notes.empty': 'No notes.',
  'sessions.edit': 'Edit',
  'sessions.edit.title': 'Title',
  'sessions.edit.notesPlaceholder': 'What happened, what is left for next time…',
  'sessions.save': 'Save',
  'sessions.cancel': 'Cancel',
  'sessions.past': 'Earlier sessions',
  'sessions.past.empty': 'No finished session yet.',
  'sessions.more': 'Show more',
  'sessions.untitled': 'Session {n}',
  'sessions.row.played': '{played} of play',
  'sessions.row.paused': '{paused} of breaks',
  'sessions.row.noPause': 'no breaks',
  'sessions.day.long': '{weekday}, {day}',
  'sessions.day.short': '{weekday} {day}',
  'sessions.range.nextDay': '{from}–{to} ({day})',
  'sessions.open': 'See the sessions',
  'sessions.open.label': 'See the sessions. {clock}',
  'sessions.next': 'Next session',
  'sessions.last': 'Last one: {day} · {played} of play',
  'sessions.row.playedLabel': 'of play',
  'sessions.month.short.0': 'Jan',
  'sessions.month.short.1': 'Feb',
  'sessions.month.short.2': 'Mar',
  'sessions.month.short.3': 'Apr',
  'sessions.month.short.4': 'May',
  'sessions.month.short.5': 'Jun',
  'sessions.month.short.6': 'Jul',
  'sessions.month.short.7': 'Aug',
  'sessions.month.short.8': 'Sep',
  'sessions.month.short.9': 'Oct',
  'sessions.month.short.10': 'Nov',
  'sessions.month.short.11': 'Dec',
  'sessions.weekday.long.0': 'Sunday',
  'sessions.weekday.long.1': 'Monday',
  'sessions.weekday.long.2': 'Tuesday',
  'sessions.weekday.long.3': 'Wednesday',
  'sessions.weekday.long.4': 'Thursday',
  'sessions.weekday.long.5': 'Friday',
  'sessions.weekday.long.6': 'Saturday',
  'sessions.weekday.short.0': 'Sun',
  'sessions.weekday.short.1': 'Mon',
  'sessions.weekday.short.2': 'Tue',
  'sessions.weekday.short.3': 'Wed',
  'sessions.weekday.short.4': 'Thu',
  'sessions.weekday.short.5': 'Fri',
  'sessions.weekday.short.6': 'Sat',
  'sessions.rating.title': 'Ratings',
  'sessions.rating.openUntil': 'Open until {day}',
  'sessions.rating.closedOn': 'Closed on {day}',
  'sessions.rating.ask': 'How was the session for you?',
  'sessions.rating.myLabel': 'Your rating for the session',
  'sessions.rating.placeholder': 'What worked, what could be better… (optional)',
  'sessions.rating.anonymous': 'Game masters read it without your name.',
  'sessions.rating.send': 'Send',
  'sessions.rating.mine': 'Your rating',
  'sessions.rating.remove': 'Remove',
  'sessions.rating.notRated': 'You didn’t rate this session.',
  'sessions.rating.aboutMe.one': 'From the game master to you',
  'sessions.rating.aboutMe.many': 'From the game masters to you',
  'sessions.rating.fromPlayers': 'The session, by the players',
  'sessions.rating.noPlayers': 'No players were in this session.',
  'sessions.rating.none': 'No player has rated it yet.',
  'sessions.rating.howMany.one': '1 of {total} rated',
  'sessions.rating.howMany.many': '{n} of {total} rated',
  'sessions.rating.anonymousGm': 'Ratings arrive without the name of who gave them.',
  'sessions.rating.players': 'The players',
  'sessions.rating.playersHint': 'Each player reads the rating and the text you give them.',
  'sessions.rating.rate': 'Rate',
  'sessions.rating.ratePlayer': 'Rate {name}',
  'sessions.rating.editPlayer': 'Edit the rating for {name}',
  'sessions.rating.notRatedPlayer': 'not rated',
  'sessions.rating.playerLabel': 'Your rating for {name}',
  'sessions.rating.playerPlaceholder': 'How did {name} play? (optional)',
  'sessions.rating.rateSession': 'Rate session {n}',
  'sessions.rating.ratePlayers': 'Rate the players of session {n}',
  'sessions.rating.badge.average': 'Players’ average: {average} out of 5',
  'sessions.rating.badge.mine': 'Your rating: {n} out of 5',
  'sessions.notice.gm': '{name} rated you in session {n} of {room}',
  'sessions.notice.players.one': 'New rating for session {n} of {room}',
  'sessions.notice.players.many': '{count} new ratings for session {n} of {room}',
  'sessions.notice.view': 'View',
  'sessions.delete': 'Delete this session…',
  'sessions.delete.title': 'Delete session {n}?',
  'sessions.delete.confirm': 'Its record goes away for everyone in the room: the time of play, the breaks, who was there, the title and the notes, along with its lines in the chat. This cannot be undone.',
  'sessions.delete.confirmOpen': 'The session in progress stops and goes away for everyone in the room: the time of play, the breaks, who is there, the title and the notes, along with its lines in the chat. This cannot be undone.',
  'sessions.delete.yes': 'Delete',
  'sessions.deleted': 'Session {n} deleted',
  'chat.tool.sendFile': 'Send file',
  'chat.file.download': 'Download',
  'chat.file.missing': '(file unavailable: {name})',
  'chat.image.missing': '(image unavailable)',
  'chat.drop.imageName': 'image',
  'chat.attached': 'Attachments',
  'chat.attached.sending': 'sending…',
  'chat.attached.remove': 'Remove {name}',
  'chat.attach.computer': 'From computer…',
  'chat.attach.cloud': 'From Desk Cloud…',
  'chat.attach.cloudTitle': 'Attach from Desk Cloud',
  'chat.attach.cloudAction': 'Attach',
  'chat.attach.cloudEmpty': 'Your Desk Cloud is still empty.',
  'chat.attach.cloudHint': 'The files get linked to the room: whoever is in it can open them.',
  'chat.input.withAttachment': 'Write something to go with it, or press Enter to send',
  'imageViewer.title': 'Image',
  'imageViewer.open': 'View full size: {name}',
  'imageViewer.openTab': 'Open in another tab',
  'chat.drop': 'Drop to send',
  'library.add': 'New item',
  'library.newSheet.default': 'New character',
  'library.newSheet': 'New sheet…',
  'library.newSheet.title': 'New sheet',
  'library.newSheet.name': 'Sheet name (optional)',
  'library.newSheet.search': 'Search system',
  'library.newSheet.systems': 'Systems',
  'library.newSheet.none': 'No system found.',
  'library.noPlugins': 'No sheet plugin installed in this room',
  'library.installPlugin': 'Install plugin…',
  'library.pluginInstalled': 'Plugin "{name}" installed in the room.',
  'library.pluginFailed': 'Could not install the plugin: {message}',
  'library.pluginUpdated': 'Plugin "{name}" updated to version {version}.',
  'library.tooLarge': 'This sheet, scene or table reached its maximum size: the last change was undone.',
  'library.open': 'Open',
  'library.openWindow': 'Open in new window',
  'library.rename': 'Rename',
  'library.moveTo': 'Move to…',
  'library.moveTo.title': 'Move "{name}" to',
  'library.moveTo.root': 'Library root',
  'library.delete': 'Delete',
  'library.delete.confirm': 'Delete "{name}"? This can\'t be undone.',
  'library.delete.folderConfirm': 'Delete the folder "{name}" and everything in it?',
  'library.mine': 'yours',
  'library.empty.gm': 'The library is empty. Use the + button to create scenes, folders and sheets and to install sheet plugins.',
  'sheet.gone': 'This sheet no longer exists.',
  'sheet.noPlugin': 'No plugin in this room edits sheets of type {type}. The sheet\'s data is kept: install the plugin again to edit it.',
  'module.charSheet': 'Sheet',
  'common.ok': 'OK',
  'common.search': 'Search',
  'common.search.clear': 'Clear search',
  'common.noResults': 'Nothing found.',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.language': 'Language',
  'library.newScene': 'New scene',
  'library.scene': 'Scene',
  'library.create': 'Create',
  'scene.addToken': 'Add a token at the center of the view',
  'scene.background': 'Choose the map image',
  'scene.mapImage': 'Map',
  'scene.bg.choose': 'Choose image…',
  'scene.bg.replace': 'Replace image…',
  'scene.bg.remove': 'Remove image',
  'scene.cols': 'Columns',
  'scene.rows': 'Rows',
  'scene.gridSize': 'Cell (px)',
  'scene.cellDistance': 'Each cell is',
  'scene.cellUnit': 'Unit',
  'scene.unit.m': 'm',
  'scene.unit.feet': 'ft',
  'scene.unit.foot': 'ft',
  'scene.gridColor': 'Grid color',
  'scene.gridOpacity': 'Grid opacity',
  'scene.gridVisible': 'Show the grid',
  'scene.fog.enabled': 'Fog of war (players only see what was revealed)',
  'scene.fog.off': 'Remove fog',
  'scene.empty': 'The GM hasn\'t set up this scene yet.',
  'scene.empty.gm': 'Choose the map image, add tokens or drop images here.',
  'scene.fit': 'Fit the map (0)',
  'scene.gone': 'This scene was deleted from the library.',
  'scene.loading': 'Loading the scene…',
  'scene.save': 'Save',
  'scene.settings': 'Scene settings',
  'scene.token': 'Token',
  'scene.token.defaultName': 'Token {n}',
  'scene.token.properties': 'Token properties',
  'scene.token.name': 'Name',
  'scene.token.image': 'Choose image…',
  'scene.token.noImage': 'No image',
  'scene.token.color': 'Color',
  'scene.token.size': 'Size',
  'scene.token.sizeCells': '{n} × {n} cells',
  'scene.token.hp': 'HP',
  'scene.token.hpMax': 'Max HP',
  'scene.token.showName': 'Show the name on the map',
  'scene.token.layer': 'Layer',
  'scene.layer.map': 'Map (below everything)',
  'scene.layer.tokens': 'Tokens',
  'scene.layer.gm': 'GM only',
  'scene.token.hidden': 'Hidden from players',
  'scene.token.controllers': 'Who can move it',
  'scene.token.nobody': 'Nobody else is at the table right now.',
  'scene.token.offline': '(person not at the table)',
  'scene.token.show': 'Show to players',
  'scene.token.hide': 'Hide from players',
  'scene.token.front': 'Bring to front',
  'scene.token.back': 'Send to back',
  'scene.token.delete': 'Delete token',
  'scene.token.deleteConfirm': 'Delete the token "{name}"?',
  'scene.token.notYours': 'Only the GM can change this token',
  'scene.tool.select': 'Select and move (V)',
  'scene.tool.pan': 'Pan the view (H)',
  'scene.tool.ruler': 'Ruler (R)',
  'scene.tool.reveal': 'Reveal fog area (F)',
  'scene.tool.hide': 'Cover area with fog',
  'scene.tool.fog': 'Fog tools',
  'scene.uploadFailed': 'Couldn\'t upload the image: {message}',
  'scene.uploading': 'Uploading…',
  'scene.viewport': 'Scene map',
  'scene.writeFailed': 'The scene rejected the change: {message}',
  'scene.zoomIn': 'Zoom in',
  'scene.zoomOut': 'Zoom out',
  'audio.nowPlaying': 'Now playing',
  'audio.nothing': 'Nothing in the playlist',
  'audio.failed': 'Couldn\'t open this track.',
  'audio.blocked': 'The browser held the table\'s sound. Click to listen.',
  'audio.seek': 'Track position',
  'audio.controls': 'Music controls',
  'audio.prev': 'Previous',
  'audio.next': 'Next',
  'audio.play': 'Play',
  'audio.pause': 'Pause',
  'audio.stop': 'Stop',
  'audio.repeat.all': 'Repeat playlist',
  'audio.repeat.one': 'Repeat this track',
  'audio.repeat.off': 'No repeat',
  'audio.gmControls': 'The GM controls the table\'s music.',
  'audio.myVolume': 'My volume',
  'audio.roomVolume': 'Table volume',
  'audio.playlist': 'Playlist',
  'audio.addFiles': 'Upload audio files…',
  'audio.addLink': 'Add by link…',
  'audio.playThis': 'Play this one',
  'audio.remove': 'Remove',
  'audio.removeNamed': 'Remove {name}',
  'audio.empty': 'The GM hasn\'t built the playlist yet.',
  'audio.empty.gm': 'Upload audio files or add links. You can also drop files here.',
  'audio.notAudio': 'None of these files is audio.',
  'audio.uploadFailed': 'Couldn\'t upload: {message}',
  'audio.link.title': 'Add by link',
  'audio.link.url': 'Audio file address (http or https)',
  'audio.link.name': 'Name shown in the list',
  'audio.link.next': 'Continue',
  'audio.link.add': 'Add',
  'audio.link.defaultName': 'Track',
  'audio.link.invalid': 'Use an address starting with http:// or https://.',
  'sfx.add': 'Add effect',
  'sfx.volume': 'Effects volume',
  'sfx.playFor': 'Play for the table',
  'sfx.play': 'Play for the table',
  'sfx.empty': 'No effects on the board.',
  'sfx.empty.gm': 'No effects yet. Use + or drop audio files here.',
  'sfx.gmOnly': 'Only GMs trigger effects.',
  'voice.inCallCount': '{n} in the call',
  'voice.outOfCall': 'not in the call',
  'voice.intro': 'Talk by voice and video with the table. Sound goes straight between computers; the RPG Desk server only introduces them.',
  'voice.join': 'Join the call',
  'voice.asking': 'Asking for the microphone…',
  'voice.leave': 'Leave the call',
  'voice.micOn': 'Turn the microphone on',
  'voice.micOff': 'Mute the microphone',
  'voice.camOn': 'Turn the camera on',
  'voice.camOff': 'Turn the camera off',
  'voice.you': '{name} (you)',
  'voice.alone': 'Nobody else has joined the call yet.',
  'voice.bigRoom': '{n} people at the table: with many people the direct call gets heavy.',
  'voice.error.mic-denied': 'The browser didn\'t allow the microphone. Grant the permission and try again.',
  'voice.error.mic-failed': 'Couldn\'t open the microphone.',
  'voice.error.cam-failed': 'Couldn\'t open the camera.',
  'voice.error.connect-failed': 'The connection to someone failed.',
  'voice.error.negotiate-failed': 'The connection to someone failed.',
  'settings.system.delete.mismatch': 'The name you typed does not match; the table was not deleted.',
  'friends.title': 'Friends & chat',
  'friends.head': 'Friends',
  'friends.summary': '{online} online',
  'friends.me.menu': 'My status',
  'friends.me.status': 'My status: {status}',
  'friends.me.online': 'Online',
  'friends.me.online.hint': 'Friends see you online. After 10 min without using RPG Desk, you show as Away.',
  'friends.me.busy': 'Busy',
  'friends.me.busy.hint': 'Friends see that you are busy. Messages arrive with no sound, no notice and without opening the chat.',
  'friends.me.offline': 'Offline',
  'friends.me.offline.hint': 'Friends see you offline. You still see your friends and get their messages.',
  'friends.me.notConnected': 'No connection to the server: your status did not change.',
  'friends.unread': '{n} unread messages',
  'friends.loading': 'Loading…',
  'friends.search': 'Search friends',
  'friends.tab.friends': 'Friends',
  'friends.tab.requests': 'Requests',
  'friends.tab.blocked': 'Blocked',
  'friends.add': 'Add friend',
  'friends.add.placeholder': 'User name',
  'friends.add.send': 'Send',
  'friends.add.suggestions': 'People with that name',
  'friends.group.room': 'At a table',
  'friends.group.online': 'Online',
  'friends.group.offline': 'Offline',
  'friends.group.hidden': 'Hidden',
  'friends.group.others': 'Other conversations',
  'friends.status.typing': 'typing…',
  'friends.status.typingName': '{name} is typing…',
  'friends.status.room': 'At the table · {room}',
  'friends.status.room.away': 'Away · {room}',
  'friends.status.room.busy': 'Busy · {room}',
  'friends.status.someRoom': 'At a table',
  'friends.status.someRoom.away': 'Away · at a table',
  'friends.status.someRoom.busy': 'Busy · at a table',
  'friends.status.away': 'Away',
  'friends.status.busy': 'Busy',
  'friends.status.online': 'Online',
  'friends.status.offline': 'Offline · {seen}',
  'friends.status.offlineOnly': 'Offline',
  'friends.status.notFriend': 'Not your friend',
  'friends.seen.now': 'seen just now',
  'friends.seen.minutes': 'seen {n} min ago',
  'friends.seen.hours': 'seen {n} h ago',
  'friends.seen.yesterday': 'seen yesterday',
  'friends.seen.days': 'seen {n} days ago',
  'friends.empty': 'You have no friends here yet. Add someone by name under Requests.',
  'friends.noMatch': 'No friend named “{q}”.',
  'friends.searchPeople': 'Look for “{q}” on RPG Desk',
  'friends.more': 'More options for {name}',
  'friends.menu.chat': 'Chat',
  'friends.menu.profile': 'View profile',
  'friends.menu.invite': 'Invite to this table',
  'friends.menu.hide': 'Hide from the list',
  'friends.menu.unhide': 'Show in the list',
  'friends.menu.remove': 'Unfriend',
  'friends.menu.block': 'Block',
  'friends.menu.add': 'Add friend',
  'friends.menu.dismiss': 'Remove from list',
  'friends.remove.confirm': 'Unfriend {name}? You stop seeing each other in the list and chatting here.',
  'friends.block.confirm': 'Block {name}? The friendship ends, and they can no longer send you messages, requests or table invites.',
  'friends.request.title': 'Add {name}',
  'friends.request.note': 'A message to go with the request (optional)',
  'friends.request.send': 'Send request',
  'friends.request.sent': 'Friend request sent to {name}.',
  'friends.requests.sent': 'Sent — {n}',
  'friends.requests.received': 'Received — {n}',
  'friends.requests.waiting': 'Waiting for an answer',
  'friends.requests.cancel': 'Cancel',
  'friends.requests.wants': 'Wants to be your friend',
  'friends.requests.accept': 'Accept request from {name}',
  'friends.requests.decline': 'Decline request from {name}',
  'friends.requests.none': 'No new requests.',
  'friends.blocked.none': 'Nobody blocked',
  'friends.blocked.hint': 'People you block can’t send you messages or table invites.',
  'friends.blocked.unblock': 'Unblock',
  'friends.chat.region': 'Chat with {name}',
  'friends.chat.log': 'Messages with {name}',
  'friends.chat.write': 'Message to {name}',
  'friends.chat.send': 'Send message',
  'friends.chat.first': 'Start the conversation with {name}.',
  'friends.chat.stranger': "You're not friends yet.",
  'friends.chat.pending': 'Friend request pending.',
  'friends.chat.friendsOnly': '{name} only gets messages from friends.',
  'friends.chat.blockedByMe': 'You blocked {name}.',
  'friends.chat.unavailable': "You can't send messages to {name}.",
  'friends.chat.minimize': 'Minimize the chat with {name}',
  'friends.chat.expand': 'Expand the chat with {name}',
  'friends.chat.close': 'Close the chat with {name}',
  'friends.chat.back': 'Back to friends',
  'friends.chat.today': 'Today',
  'friends.chat.yesterday': 'Yesterday',
  'friends.invite.title': 'Invite friends to {room}',
  'friends.invite.send': 'Invite',
  'friends.invite.sendN': 'Invite ({n})',
  'friends.invite.done': '{n} invite(s) sent.',
  'friends.invite.alreadyHere': 'Already at the table',
  'friends.invite.noFriends': 'You have no friends to invite yet. Add someone under Friends & chat → Requests.',
  'friends.invite.received': '{name} invited you to the table {room}',
  'friends.invite.sent': 'You invited them to the table {room}',
  'friends.invite.notice': 'invited you to the table {room}',
  'friends.invite.enter': 'Join',
  'friends.invite.here': 'You are at this table',
  'friends.invite.gone': 'This table no longer exists.',
  'friends.invite.locked': 'Password-protected table',
  'friends.invite.passwordTitle': 'Join {room}',
  'friends.invite.password': 'This table has a password. Ask whoever invited you for it.',
  'profile.title': 'Profile',
  'profile.dialog': "{name}'s profile",
  'profile.chat': 'Chat',
  'profile.addContact': 'Add friend',
  'profile.relation.none': "You're not friends yet",
  'profile.relation.sent': 'Friend request sent',
  'profile.relation.pending': 'Friend request pending',
  'profile.relation.friend': "You're friends",
  'profile.relation.hidden': 'Hidden from your friends list',
  'profile.relation.blocked': 'You blocked {name}',
  'profile.menu.dismiss': 'Remove from conversations',
  'profile.menu.block': 'Block {name}…',
  'profile.level': 'Level {n}',
  'profile.since': 'On RPG Desk since {date}',
  'profile.online': 'Online now',
  'profile.seen.now': 'Seen just now',
  'profile.seen.minutes': 'Seen {n} min ago',
  'profile.seen.hours': 'Seen {n} h ago',
  'profile.seen.yesterday': 'Seen yesterday',
  'profile.seen.days': 'Seen {n} days ago',
  'profile.rating.title': 'Player ratings',
  'profile.rating.mine': 'How players rate you',
  'profile.vote': 'Your rating of {name}',
  'profile.like': 'Like',
  'profile.dislike': 'Dislike',
  'profile.voted.like': 'You liked',
  'profile.voted.dislike': "You didn't like",
  'profile.voted.none': "You haven't rated yet",
  'profile.raters.none': 'Nobody has rated {name} yet',
  'profile.raters.one': '1 player rated {name}',
  'profile.raters.many': '{n} players rated {name}',
  'profile.tables.title': 'At the tables',
  'profile.tables.asPlayer': 'As a player',
  'profile.tables.asGm': 'As a game master',
  'profile.tables.player.one': '1 rating from a game master',
  'profile.tables.player.many': '{n} ratings from game masters',
  'profile.tables.gm.one': '1 rating from a player',
  'profile.tables.gm.many': '{n} ratings from players',
  'profile.tables.few': '{count} · the average shows from {min}',
  'stars.one': '1 star',
  'stars.many': '{n} stars',
  'stars.of': '{n} out of 5 stars',
  'stars.average': 'Average: {average} out of 5 stars',
  'profile.likes.one': 'like',
  'profile.likes.many': 'likes',
  'profile.dislikes.one': 'dislike',
  'profile.dislikes.many': 'dislikes',
  'profile.bio': 'About',
  'profile.rpg': 'Likes to play',
  'profile.times': 'Usually plays',
  'profile.emptyAll': "{name} hasn't written anything in their profile yet.",
  'profile.mine.title': 'Your profile',
  'profile.mine.filled': '{n} of 3 filled in',
  'profile.mine.footer': 'This is how other players see you.',
  'profile.add.bio': 'Tell a bit about yourself as a player or GM',
  'profile.add.rpg': 'Add the systems you like to play',
  'profile.add.times': 'Say when you usually play',
  'profile.placeholder.bio': 'E.g.: GM for 10 years, love long campaigns',
  'profile.edit': 'Edit {field}',
  'profile.cancel': 'Cancel',
  'profile.save': 'Save',
  'profile.changeAvatar': 'Change avatar',
  'profile.cover.change': 'Change cover',
  'profile.cover.pick': 'Choose image…',
  'profile.cover.remove': 'Remove cover',
  'profile.cover.title': 'Profile cover',
  'profile.cover.saved': 'Cover changed.',
  'profile.cover.removed': 'Cover removed.',
  'profile.avatar.title': 'Profile picture',
  'profile.avatar.uploadHint': 'The image becomes your picture, cropped to a square and shrunk.',
  'cloud.title': 'Desk Cloud',
  'cloud.view.all': 'All files',
  'cloud.view.recent': 'Recent',
  'cloud.view.favorites': 'Favorites',
  'cloud.view.trash': 'Trash',
  'cloud.view.unlinked': 'Only you · no room',
  'cloud.section.rooms': 'Rooms',
  'cloud.section.folders': 'Folders',
  'cloud.section.storage': 'Storage',
  'cloud.storage.of': '{used} of {quota}',
  'cloud.storage.maxFile': 'Files up to {size} each',
  'cloud.storage.plans': 'See plans · up to {size}',
  'cloud.storage.plansOnly': 'See plans',
  'cloud.storage.full': 'Space full: delete files or change your plan',
  'cloud.storage.aria': '{used} of {quota} used',
  'cloud.storage.open': 'See your plan',
  'cloud.kind.map': 'Map',
  'cloud.kind.token': 'Token',
  'cloud.kind.audio': 'Audio',
  'cloud.kind.doc': 'Document',
  'cloud.kind.other': 'Other',
  'cloud.kinds.all': 'All',
  'cloud.kinds.map': 'Maps',
  'cloud.kinds.token': 'Tokens',
  'cloud.kinds.audio': 'Audio',
  'cloud.kinds.doc': 'Documents',
  'cloud.kinds.other': 'Other',
  'cloud.kinds.photo': 'Photos',
  'cloud.kinds.trash': 'Trash',
  'cloud.root': 'the top level',
  'cloud.files': '{n} files',
  'cloud.files.one': '1 file',
  'cloud.subfolders': '{n} folders',
  'cloud.subfolders.one': '1 folder',
  'cloud.selected': '{n} selected',
  'cloud.selected.one': '1 selected',
  'cloud.subtitle': '{files} · {size}',
  'cloud.subtitle.room': "this room's {n} players can open them",
  'cloud.subtitle.room.one': "this room's player can open them",
  'cloud.subtitle.roomEmpty': 'nobody else has joined this room yet',
  'cloud.subtitle.unlinked': 'files no room sees',
  'cloud.subtitle.trash': 'gone for good after 30 days',
  'cloud.upload': 'Upload files',
  'cloud.newFolder': 'New folder',
  'cloud.openRoom': 'Open room',
  'cloud.search': 'Search in {where}',
  'cloud.search.clear': 'Clear search',
  'cloud.filter': 'Filter by type',
  'cloud.sort': 'Sort',
  'cloud.sort.recent': 'Newest',
  'cloud.sort.name': 'Name (A–Z)',
  'cloud.sort.size': 'Largest first',
  'cloud.layout': 'View',
  'cloud.layout.grid': 'Grid',
  'cloud.layout.list': 'List',
  'cloud.selectAll': 'Select all',
  'cloud.clearSelection': 'Clear selection',
  'cloud.select': 'Select {name}',
  'cloud.bulk': 'Actions for the selected files',
  'cloud.col.name': 'Name',
  'cloud.col.kind': 'Type',
  'cloud.col.access': 'Who can open',
  'cloud.col.size': 'Size',
  'cloud.col.date': 'Modified',
  'cloud.folders': 'Folders',
  'cloud.filesHeading': 'Files',
  'cloud.folderTile': '{files} · {size}',
  'cloud.dropTile': 'Upload files',
  'cloud.dropTile.hint': 'or drag them here from your computer',
  'cloud.drop': 'Drop to upload to {where}',
  'cloud.drop.hint': 'Maps, tokens, audio and PDFs · up to {size} per file',
  'cloud.drop.cloud': 'your Desk Cloud',
  'cloud.loadMore': 'Load more',
  'cloud.empty.title': 'Nothing here yet',
  'cloud.empty.text': 'Upload maps, tokens, tracks and PDFs, or drag them from your computer to this area.',
  'cloud.empty.search': 'Nothing found',
  'cloud.empty.searchText': 'No file with “{q}” in {where}.',
  'cloud.empty.trash': 'The trash is empty',
  'cloud.empty.trashText': 'Files you move to the trash stay here for 30 days before they are gone for good.',
  'cloud.empty.favorites': 'No favorites',
  'cloud.empty.favoritesText': "Tap a file's star to find it quickly here.",
  'cloud.empty.kind': 'No files of this type',
  'cloud.empty.kindText': 'Try another filter.',
  'cloud.empty.pick': 'Choose files',
  'cloud.trashNote': 'Items in the trash are deleted for good after 30 days and count toward your space until then.',
  'cloud.access.mine': 'Only you',
  'cloud.access.rooms': '{n} rooms',
  'cloud.access.title': 'Rooms: {rooms}',
  'cloud.access.titleMine': 'Only you can open it',
  'cloud.fav.add': 'Add to favorites',
  'cloud.fav.remove': 'Remove from favorites',
  'cloud.listen': 'Listen to {name}',
  'cloud.pause': 'Pause {name}',
  'cloud.more': 'More actions for {name}',
  'cloud.today': 'Today, {time}',
  'cloud.yesterday': 'Yesterday',
  'cloud.details': 'Details',
  'cloud.details.close': 'Close details',
  'cloud.details.preview': 'Open the preview of {name}',
  'cloud.details.open': 'Open',
  'cloud.details.rename': 'Rename',
  'cloud.details.newName': 'New name',
  'cloud.details.save': 'Save',
  'cloud.details.use': 'Use in a room',
  'cloud.details.useIn': 'in',
  'cloud.details.noRoom': 'This file is not in any room yet.',
  'cloud.details.link': 'Link to a room',
  'cloud.details.useNone': 'No actions for this type of file in your rooms.',
  'cloud.details.access': 'Who can open',
  'cloud.details.manage': 'Manage',
  'cloud.details.you': 'You',
  'cloud.details.owner': 'Owner',
  'cloud.details.roomLabel': 'Room',
  'cloud.details.players': '{n} players can open it',
  'cloud.details.players.one': '1 player can open it',
  'cloud.details.info': 'Information',
  'cloud.details.type': 'Type',
  'cloud.details.size': 'Size',
  'cloud.details.dimensions': 'Dimensions',
  'cloud.details.duration': 'Duration',
  'cloud.details.folder': 'Folder',
  'cloud.details.sent': 'Uploaded',
  'cloud.details.trashLeft': 'Gone for good in {n} days.',
  'cloud.details.trashLeft.one': 'Gone for good tomorrow.',
  'cloud.details.trashToday': 'Gone for good today.',
  'cloud.details.pixels': '{w} × {h} px',
  'cloud.preview': 'Preview of {name}',
  'cloud.preview.close': 'Close the preview',
  'cloud.preview.prev': 'Previous file',
  'cloud.preview.next': 'Next file',
  'cloud.preview.strip': 'Other files',
  'cloud.preview.onlyYou': 'Only you hear this preview',
  'cloud.preview.none': 'No preview for .{ext}',
  'cloud.share.title': 'Who can open',
  'cloud.share.you': 'You',
  'cloud.share.youHint': 'Owner · can always open it',
  'cloud.share.gmRooms': 'Your rooms',
  'cloud.share.playerRooms': 'Rooms where you play',
  'cloud.share.gmHint': '{n} players · you are a game master',
  'cloud.share.playerHint': 'you play · you can only unlink',
  'cloud.share.noRooms': 'You are not a game master in any room yet.',
  'cloud.share.note': 'Players in the checked rooms can open the file. It stays in your Desk Cloud: if the room is deleted, the file is only yours again.',
  'cloud.share.saved': 'Access updated',
  'cloud.share.files': '{n} files',
  'cloud.folder.name': 'Folder name',
  'cloud.folder.create': 'Create',
  'cloud.folder.pick': 'Move to',
  'cloud.folder.pickFolder': 'Move the folder “{name}” to',
  'cloud.folder.move': 'Move here',
  'cloud.folder.inside': 'Folders inside {name}',
  'cloud.folder.deleteConfirm': 'Delete the folder “{name}” and the folders inside it? Their files go to the trash, where you can restore them for 30 days.',
  'cloud.uploads': 'Uploads',
  'cloud.uploads.sending': 'Uploading {n} files…',
  'cloud.uploads.sending.one': 'Uploading 1 file…',
  'cloud.uploads.done': '{n} uploaded',
  'cloud.uploads.done.one': '1 uploaded',
  'cloud.uploads.failed': '{n} not uploaded',
  'cloud.uploads.failed.one': '1 not uploaded',
  'cloud.uploads.collapse': 'Collapse the uploads',
  'cloud.uploads.expand': 'Show the uploads',
  'cloud.uploads.close': 'Close the uploads',
  'cloud.uploads.waiting': 'Waiting',
  'cloud.uploads.sent': 'Uploaded',
  'cloud.uploads.sentTo': 'In {room}',
  'cloud.uploads.error': 'Not uploaded',
  'cloud.uploads.tooLarge': '{size}: larger than the {limit} limit of {plan}.',
  'cloud.uploads.noSpace': 'Your space is full.',
  'cloud.uploads.plansWith': 'See plans with files up to {size}',
  'cloud.uploads.plans': 'See plans',
  'cloud.uploads.cancel': 'Cancel',
  'cloud.uploads.retry': 'Try again',
  'cloud.uploads.reveal': 'Show the file',
  'cloud.uploads.progress': 'Uploading {name}',
  'cloud.menu.open': 'Open',
  'cloud.menu.use': 'Use in a room',
  'cloud.menu.download': 'Download',
  'cloud.menu.zip': 'Download .zip',
  'cloud.menu.copyLink': 'Copy link',
  'cloud.menu.rename': 'Rename',
  'cloud.menu.moveTo': 'Move to',
  'cloud.menu.pickFolder': 'Choose folder…',
  'cloud.menu.toRoot': 'Take out of the folder',
  'cloud.menu.kind': 'Type',
  'cloud.menu.favorite': 'Favorite',
  'cloud.menu.unfavorite': 'Remove from favorites',
  'cloud.menu.share': 'Who can open…',
  'cloud.menu.trash': 'Move to trash',
  'cloud.menu.restore': 'Restore',
  'cloud.menu.purge': 'Delete forever',
  'cloud.menu.emptyTrash': 'Empty the trash',
  'cloud.menu.newFolderHere': 'New folder here',
  'cloud.menu.uploadHere': 'Upload files here',
  'cloud.menu.moveFolder': 'Move to…',
  'cloud.menu.deleteFolder': 'Delete folder',
  'cloud.menu.openRoom': 'Open the room',
  'cloud.menu.uploadToRoom': 'Upload files to this room',
  'cloud.menu.zipRoom': 'Download .zip (yours linked to it)',
  'cloud.menu.selectAll': 'Select all',
  'cloud.menu.sortBy': 'Sort by',
  'cloud.menu.viewAs': 'View as',
  'cloud.menu.reveal': 'Show in Desk Cloud',
  'cloud.menu.unlink': 'Remove from this room',
  'cloud.use.scene': 'Open as a new scene',
  'cloud.use.background': 'Use as the open scene background',
  'cloud.use.token': 'Put on the open scene',
  'cloud.use.play': 'Play for the room',
  'cloud.use.playlist': 'Add to the playlist',
  'cloud.use.chat': 'Send in the chat',
  'cloud.use.linked': 'The players of {room} can open the file now.',
  'cloud.use.noScene': 'Open a scene in the room to use the file there.',
  'cloud.use.failed': 'Could not use the file in the room.',
  'cloud.use.done.scene': '“{name}” opened as a scene',
  'cloud.use.done.background': '“{name}” is the scene background',
  'cloud.use.done.token': '“{name}” put on the scene',
  'cloud.use.done.play': 'Playing “{name}” for the room',
  'cloud.use.done.playlist': '“{name}” added to the playlist',
  'cloud.use.done.chat': '“{name}” sent in the chat',
  'cloud.toast.trashedOne': '“{name}” moved to the trash.',
  'cloud.toast.trashed': '{n} files moved to the trash.',
  'cloud.toast.trashedRooms': 'The rooms no longer see them.',
  'cloud.toast.restoredOne': '“{name}” restored.',
  'cloud.toast.restored': '{n} files restored.',
  'cloud.toast.moved': '{n} moved to {folder}.',
  'cloud.toast.folderDeleted': 'Folder “{name}” deleted; {n} files moved to the trash.',
  'cloud.toast.linkCopied': 'Link copied. It expires after a few hours.',
  'cloud.undo': 'Undo',
  'cloud.copyManually': 'Copy the link below (it expires after a few hours):',
  'cloud.purge.title': 'Delete forever',
  'cloud.purge.one': 'Delete “{name}” forever? Maps, songs and messages that use the file stop showing it. This cannot be undone.',
  'cloud.purge.many': 'Delete {n} files forever? Maps, songs and messages that use them stop showing them. This cannot be undone.',
  'cloud.purge.all': 'Delete the {n} files in the trash forever? This cannot be undone.',
  'cloud.where': 'Place',
  'cloud.where.pick': 'Choose the place',
  'cloud.mobile.select': 'Select',
  'cloud.panel.title': 'Files',
  'cloud.panel.search': "Search the room's files",
  'cloud.panel.dragHint': 'drag onto the scene',
  'cloud.panel.onScene': 'On the scene',
  'cloud.panel.playingAll': 'Playing for everyone',
  'cloud.panel.pauseAll': 'Pause for everyone',
  'cloud.panel.playAll': 'Play {name} for everyone',
  'cloud.panel.toChat': 'Send {name} in the chat',
  'cloud.panel.whole': 'All of Desk Cloud',
  'cloud.panel.send': 'Upload',
  'cloud.panel.sendHint': 'Uploads from here are already linked to this room',
  'cloud.panel.empty': 'No files linked to this room yet.',
  'cloud.panel.emptyPlayer': "Your files linked to this room show up here. The game master's arrive through the scene, the chat and the music.",
  'cloud.panel.pickTitle': 'Link files to this room',
  'cloud.panel.pickAction': 'Link',
  'cloud.panel.pickEmpty': 'All your files are already in this room.',
  'cloud.pick.emptyFolder': 'No files to pick in this folder.',
  'cloud.panel.unlinked': '“{name}” left this room.',
  // Configurações (docs/spec/settings.md)
  'prefs.sections': 'Settings sections',
  'prefs.me': 'View and edit your profile',
  'prefs.me.mobile': 'Profile, avatar and bio',
  'prefs.search': 'Search settings',
  'prefs.search.title': 'Results',
  'prefs.search.count': 'Results: {n}',
  'prefs.search.none': 'Nothing found. Try “sound”, “dice” or “language”.',
  'prefs.search.section': 'Section',
  'prefs.back': 'Back',
  'prefs.backTo': 'Back to Settings',
  'prefs.saved': 'Preference saved.',
  'prefs.scope.account': 'Saved to your account',
  'prefs.scope.account.tip': 'Applies on any device you sign in to.',
  'prefs.scope.device': 'Saved on this computer',
  'prefs.scope.device.tip': 'Kept only in this browser.',
  'prefs.reset': 'Restore the defaults for {section}',
  'prefs.reset.confirm': 'Put {section} back to the defaults?',
  'prefs.reset.yes': 'Yes, restore',
  'prefs.reset.done': '{section} defaults restored.',
  'prefs.summary.myColors': 'My colors',
  'prefs.summary.default': 'Default',
  'prefs.group.account': 'Account',
  'prefs.group.preferences': 'Preferences',
  'prefs.group.support': 'Support',
  'prefs.profile.title': 'Profile',
  'prefs.profile.sub': 'How other players see you in rooms and among friends.',
  'prefs.profile.kw': 'profile photo bio about public',
  'prefs.profile.avatar.kw': 'photo picture image',
  'prefs.profile.cover.kw': 'cover background header banner image',
  'prefs.profile.bio.placeholder': 'Tell others a bit about you as a player or game master',
  'prefs.profile.rpg.placeholder': 'E.g. D&D 5e, Pathfinder, Call of Cthulhu',
  'prefs.profile.times.placeholder': 'E.g. as the game master, on Saturday nights',
  'prefs.profile.preview': 'How others see you',
  'prefs.profile.empty': 'Not filled in yet',
  'prefs.profile.open': 'Open my public profile',
  'prefs.profile.unsaved': 'You have profile changes that are not saved.',
  'prefs.profile.discard': 'Discard',
  'prefs.profile.save': 'Save profile',
  'prefs.account.title': 'Account and security',
  'prefs.account.sub': 'Your account sign-in details and privacy.',
  'prefs.account.kw': 'account login sign in access security privacy',
  'prefs.account.access': 'Access',
  'prefs.account.since': 'Member since',
  'prefs.account.password': 'Password',
  'prefs.account.password.desc': 'Use a password you do not use on other sites.',
  'prefs.account.password.kw': 'password change',
  'prefs.account.privacy': 'Privacy',
  'prefs.account.imsAnyone': "Get messages from people who aren't your friends",
  'prefs.account.imsAnyone.desc': 'When off, only your friends and the people you wrote to can message you.',
  'prefs.account.imsAnyone.kw': 'direct message privacy friends strangers chat conversation spam',
  'prefs.account.terms.desc': 'The rules for using RPG Desk, which you accepted when you created your account.',
  'prefs.account.terms.kw': 'terms of use rules contract conditions',
  'prefs.account.privacyPolicy.desc': 'What data RPG Desk keeps, for how long, and how to ask for a copy or for it to be deleted.',
  'prefs.account.privacyPolicy.kw': 'privacy policy personal data gdpr lgpd ccpa deletion copy',
  'prefs.account.read': 'Read',
  'prefs.account.danger': 'Danger zone',
  'prefs.account.delete': 'Cancel the account',
  'prefs.account.delete.desc': 'Closes your RPG Desk account. RPG Desk asks for your password first.',
  'prefs.account.delete.kw': 'delete remove close account',
  'prefs.account.delete.confirm': 'Cancel the account?',
  'prefs.plan.title': 'Plan',
  'prefs.plan.sub': 'Your plan, the space it gives and how the plans compare.',
  'prefs.plan.kw': 'plan space storage files limit',
  'prefs.general.title': 'General',
  'prefs.general.sub': 'Language, start screen, rooms and scenes.',
  'prefs.general.language.kw': 'language portuguese english',
  'prefs.general.explore': 'Explore',
  'prefs.general.adult': 'Rooms for adults (18+)',
  'prefs.general.adult.desc': 'How rooms marked 18+ show up in Explore.',
  'prefs.general.adult.kw': 'adult mature 18 content explore',
  'prefs.general.adult.hide': 'Hide',
  'prefs.general.adult.show': 'Show with a warning',
  'prefs.general.table': 'Virtual table',
  'prefs.general.distance': 'Distances',
  'prefs.general.distance.kw': 'ruler measure unit meters feet movement tokens scene',
  'prefs.general.distance.scene': 'The scene’s unit',
  'prefs.general.distance.m': 'Meters',
  'prefs.general.distance.ft': 'Feet',
  'prefs.general.distance.both': 'Meters and feet',
  'prefs.general.distance.desc.scene': 'Ruler and token movement, in the unit set in each scene.',
  'prefs.general.distance.desc.m': 'Ruler and token movement. Example: 6 m.',
  'prefs.general.distance.desc.ft': 'Ruler and token movement. Example: 20 ft (5 ft = 1.5 m).',
  'prefs.general.distance.desc.both': 'Ruler and token movement. Example: 6 m (20 ft).',
  'prefs.chat.title': 'Chat',
  'prefs.chat.sub': 'How room messages read and are formatted.',
  'prefs.chat.preview': 'Live preview',
  'prefs.chat.preview.hint': 'Follows the settings below',
  'prefs.chat.preview.gm': 'Game master',
  'prefs.chat.preview.player': 'Bruna',
  'prefs.chat.preview.action': 'lights a torch and looks around.',
  'prefs.chat.preview.bar': 'Lia — HP 18 → 12',
  'prefs.chat.reading': 'Reading',
  'prefs.chat.size.kw': 'font letter size text bigger smaller',
  'prefs.chat.timestamps.kw': 'time clock timestamp',
  'prefs.chat.formatting': 'Message formatting',
  'prefs.chat.smileys.kw': 'emoji emoticon smiley',
  'prefs.chat.table': 'In the room',
  'prefs.chat.bars': 'Post a chat notice when the players’ bars change',
  'prefs.chat.bars.desc': 'For example HP, mana or another bar from the sheet.',
  'prefs.chat.bars.kw': 'bar life hp mana notice',
  'prefs.chat.friends': 'Friends & chat panel',
  'prefs.dice.title': 'Dice',
  'prefs.dice.sub': 'How your rolls look, move and sparkle.',
  'prefs.dice.kw': 'dice roll d20 3d graphics acceleration gpu webgl',
  'prefs.dice.look': 'Your dice’s look',
  'prefs.dice.look.kw': 'color colors number customize customization texture image picture zoom print size scale',
  'prefs.dice.use': 'Use my colors',
  'prefs.dice.use.desc': 'Other players see your dice in these colors and with the texture.',
  'prefs.dice.otherColor': 'another color',
  'prefs.dice.preview': 'Preview of the d20 in your colors',
  'prefs.dice.resetColors': 'Restore the default colors',
  'prefs.dice.texture': 'Texture',
  'prefs.dice.texture.desc': 'An image on every face, with the number on top.',
  'prefs.dice.texture.pick': 'Choose image…',
  'prefs.dice.texture.remove': 'Remove',
  'prefs.dice.texture.title': 'Dice texture',
  'prefs.dice.textureScale': 'Print size',
  'prefs.dice.textureScale.desc': 'Below 100%, the image repeats on each face; above it, the face shows only its center.',
  'prefs.dice.texture.broken': 'The image does not open: it left the Desk Cloud or the browser cannot read it. The dice roll with the colors only.',
  'prefs.dice.texture.refused': 'Your last roll went without the texture: the image went to the trash or no longer fits (PNG, JPEG, WebP or GIF, up to {max}).',
  'prefs.dice.display': 'Display',
  'prefs.dice.noLighting': 'No-lighting mode',
  'prefs.dice.noLighting.desc': 'Use it if the 3D dice are slow or glitchy.',
  'prefs.dice.noLighting.kw': 'lighting light shadow slow lag glitch video graphics performance',
  'prefs.dice.effects.kw': 'effect fire ice acid stone tavern casino nature divine light lightning electric thunder critical',
  'prefs.dice.effects.when': 'When',
  'prefs.dice.effects.show': 'show',
  'prefs.notify.title': 'Notifications',
  'prefs.notify.sub': 'Sounds and notices for what happens while you play.',
  'prefs.notify.banner.title': 'System notifications are off',
  'prefs.notify.banner.text': 'Without them, you get no notices while RPG Desk is in the background.',
  'prefs.notify.banner.blocked': 'The browser blocked notifications for RPG Desk. To allow them, use the site permissions (the lock next to the address).',
  'prefs.notify.banner.allow': 'Allow notifications',
  'prefs.notify.volume': 'Overall volume',
  'prefs.notify.volume.kw': 'volume sound loud quiet',
  'prefs.notify.listen': 'Listen: {name}',
  'prefs.notify.sound.kw': 'sound sounds alert play',
  'prefs.notify.soundScene': 'Scene sounds',
  'prefs.notify.soundScene.desc': 'Doors opening, closing and locked.',
  'prefs.notify.messages': 'Notify new direct messages',
  'prefs.notify.unfocused': 'In the background',
  'prefs.notify.flash': 'Flash the window title',
  'prefs.notify.flash.desc': 'When something arrives while RPG Desk is in the background.',
  'prefs.av.title': 'Audio and video',
  'prefs.av.sub': 'Devices, and how you join the room’s call.',
  'prefs.av.kw': 'conference call voice video',
  'prefs.av.test': 'Quick test',
  'prefs.av.test.desc': 'Check your camera and microphone before joining the call.',
  'prefs.av.test.kw': 'test microphone camera preview webcam',
  'prefs.av.camera.preview': 'Camera preview',
  'prefs.av.camera.off': 'Camera off',
  'prefs.av.camera.start': 'Turn the preview on',
  'prefs.av.camera.stop': 'Turn the preview off',
  'prefs.av.camera.error': 'Could not open the camera.',
  'prefs.av.mic.level': 'Microphone level',
  'prefs.av.mic.idle': 'Click test and say something: the bar should move.',
  'prefs.av.mic.live': 'Say something: the bar should move.',
  'prefs.av.devices': 'Devices',
  'prefs.av.missing': 'Disconnected device',
  'prefs.av.speaker': 'Audio output',
  'prefs.av.speaker.desc': 'The headphones or speaker for the call’s voices.',
  'prefs.av.speaker.kw': 'headphones speaker headset sound output',
  'prefs.av.speaker.test': 'Play a test sound on the chosen output',
  'prefs.av.speaker.error': 'Could not play on that output.',
  'prefs.av.join': 'When joining the call',
  'prefs.support.title': 'Troubleshooting',
  'prefs.support.sub': 'Diagnostics and options to start over.',
  'prefs.support.kw': 'help problem error bug support advanced utilities',
  'prefs.support.diagnostics': 'Diagnostics',
  'prefs.support.log': 'Error log',
  'prefs.support.log.kw': 'log error bug download',
  'prefs.support.log.download': 'Download the log',
  'prefs.support.dice': '3D dice slow or glitchy?',
  'prefs.support.dice.desc': 'Turn on the no-lighting mode, in the Dice section.',
  'prefs.support.dice.go': 'Go to Dice',
  'prefs.support.resetAll': 'Restore all preferences',
  'prefs.support.resetAll.desc': 'Chat, dice, notifications, audio and everything else on this computer go back to the defaults. The language stays.',
  'prefs.support.resetAll.kw': 'reset defaults factory',
  'prefs.support.resetAll.button': 'Restore…',
  'prefs.support.resetAll.confirm': 'Restore all preferences?',
  'prefs.support.resetAll.yes': 'Yes, restore',
  'prefs.support.resetAll.done': 'Preferences restored.',
  'prefs.support.layouts': 'Forget the room arrangements',
  'prefs.support.layouts.kw': 'layout panels tabs spaces modules arrangement',
  'prefs.support.layouts.button': 'Forget…',
  'prefs.support.layouts.confirm': 'Forget the arrangements of every room?',
  'prefs.support.layouts.yes': 'Yes, forget',
  'prefs.support.layouts.done': 'Room arrangements forgotten.',
  'config.plan.current': 'Your plan',
  'config.plan.until': 'Valid until {date}',
  'config.plan.noExpiry': 'No end date',
  'config.plan.used': '{used} of {total} used',
  'config.plan.compare': 'The plans',
  'config.plan.howTo': "To change your plan, talk to the server's administrators.",
  'config.plan.yours': 'yours',
  'config.plan.entitlement': 'What it gives',
  'config.general.app': 'App',
  'config.general.startPage': 'When signing in, open',
  'config.general.friends.show': 'Show the Friends & chat panel',
  'config.general.friends.hint': 'The panel sits in the bottom-right corner on every screen. Turned off, it disappears along with the chat windows; messages from friends still arrive and are kept, and notices follow Notifications.',
  'config.general.chatSize': 'Text size',
  'config.general.chatSpacing': 'Line spacing',
  'config.general.spacing.normal': 'Normal',
  'config.general.spacing.compact': 'Compact',
  'config.general.timestamps': 'Show message times',
  'config.general.dice3d': 'Show 3D dice on rolls',
  'config.account.name': 'Account name',
  'config.account.changePassword': 'Change password',
  'config.account.current': 'Current password',
  'config.account.new': 'New password',
  'config.account.again': 'Repeat the new password',
  'config.account.rule': 'Use at least 8 characters.',
  'config.account.mismatch': 'The two new passwords don\'t match.',
  'config.account.passwordChanged': 'Password changed.',
  'config.account.delete': 'Cancel the account…',
  'config.account.deleteForever': 'Delete my account',
  'config.account.deleteWarning': 'This deletes your account, the tables you created, your direct messages, your files and your contacts. What you sent to other people\'s tables stays there. There\'s no going back.',
  'config.account.confirmPassword': 'Confirm with your password',
  'config.profile.loading': 'Loading your profile…',
  'config.profile.saved': 'Profile saved.',
  'config.notify.sounds': 'Sounds',
  'config.notify.soundMessages': 'Direct message received',
  'config.notify.soundMentions': 'Someone mentions me with @ at the table',
  'config.notify.soundPvt': 'Private conversation at the table',
  'config.notify.soundKick': 'I was kicked or banned from a table',
  'config.notify.soundRolls': 'Dice rolls',
  'config.notify.test': 'Listen',
  'config.notify.visual': 'Notices',
  'config.notify.always': 'Always',
  'config.notify.never': 'Never',
  'config.text.hint': 'How other people\'s text markup shows up for you.',
  'config.text.headings': 'Lines starting with # become headings',
  'config.text.lines': 'Lines with --- become separators',
  'config.text.smileys': 'Turn :) and :sorriso: into emoji',
  'config.text.initialCaps': 'Drop cap with {L} at the start',
  'config.conf.mic': 'Microphone',
  'config.conf.camera': 'Camera',
  'config.conf.default': 'System default',
  'config.conf.test': 'Test the microphone',
  'config.conf.stopTest': 'Stop the test',
  'config.conf.micOn': 'Join with the microphone on',
  'config.conf.camOn': 'Join with the camera on',
  'config.plugins.title': 'Table plugins',
  'config.plugins.hint': 'Plugins (sheets and tables in HTML) are installed per table, and everyone at the table uses its plugins. Here the GM installs and removes plugins and enables the tables they bring.',
  'config.plugins.version': 'version {v}',
  'config.plugins.none': 'The table {room} has no plugins yet.',
  'config.plugins.hint.player': 'Plugins (sheets and tables in HTML) are installed per table, and everyone at the table uses its plugins. Only GMs install and remove plugins and enable tables.',
  'config.plugins.install': 'Install plugin…',
  'config.plugins.remove': 'Remove',
  'config.plugins.remove.title': 'Remove the plugin "{name}"',
  'config.plugins.remove.noSheets': 'No sheet uses this plugin.',
  'config.plugins.remove.oneSheet': '1 sheet uses this plugin.',
  'config.plugins.remove.sheets': '{count} sheets use this plugin.',
  'config.plugins.remove.keepSheets': 'The sheets are not deleted: they stay in the library, read-only, until the plugin is installed again.',
  'config.plugins.remove.docks': 'Its active tables leave the room, with their data kept.',
  'config.plugins.remove.go': 'Remove plugin',
  'config.plugins.tables': '{name} tables',
  'config.plugins.official': 'Official',
  'config.plugins.officialHint': 'Installed from the RPG Desk official plugin catalog',
  'config.tables.active': 'Enabled',
  'config.tables.stored': 'Disabled, data kept',
  'config.tables.off': 'Disabled',
  'config.tables.enable': 'Enable',
  'config.tables.disable': 'Disable',
  'config.tables.erase': 'Erase data',
  'config.tables.erase.label': 'Erase data kept for: {type}',
  'config.utils.debugHint': 'If something goes wrong, save the log and send it to whoever runs the server.',
  'config.utils.logCount': '{n} entry(ies) in this session',
  'config.advanced.reset': 'Start over',
  'config.advanced.hint': 'Preferences and table arrangements are stored on this computer.',
  'config.advanced.resetLayoutsConfirm': 'Each table\'s modules go back to the initial arrangement.',
  'config.text.sample': '{I}t was a cold night on the road :)\n\n---\n\n# The tavern\nThe dwarf *raises the mug* and says "Cheers!" :sorriso:',
  'library.setMain': 'Use as my main character',
  'library.unsetMain': 'Stop using as my main character',
  'library.main': 'main',
  'users.menu.openSheet': 'Open {name}\'s sheet',
  'users.menu.card': 'Card bars and lines…',
  'users.menu.roomAvatar': 'Change my photo at this table…',
  'users.menu.roomAvatarOf': "Change {name}'s photo at this table…",
  'users.menu.roomAvatarRemove': 'Go back to my profile photo',
  'users.menu.roomAvatarRemoveOf': "Remove {name}'s photo at this table",
  'users.roomAvatar.saved': 'Photo for this table changed',
  'users.roomAvatar.removed': 'The profile photo is back at this table',
  'users.bar.empty': 'empty',
  'users.bar.editHint': 'double-click to change',
  'users.bar.edit': 'Change {name}',
  'users.line.editHint': 'double-click to write',
  'users.line.edit': 'Write in {name}',
  'users.barEditor.value': 'Current value',
  'users.barEditor.max': 'Maximum',
  'users.barEditor.hint': '+5 adds, -3 takes away, =10 sets the number. Enter saves, Esc cancels.',
  'users.menu.noMainCharacter': 'Stop using a main character',
  'users.menu.createSheet': 'Create a sheet for {name}…',
  'users.menu.createAnotherSheet': 'Create another sheet for {name}…',
  'users.menu.giveSheet': 'Give {name} a sheet from the library…',
  'users.menu.chooseMain': "Choose {name}'s main character…",
  'users.giveSheet.prompt': "The sheet becomes {name}'s: only they and the GMs can edit it.",
  'users.chooseMain.prompt': "The main character shows on {name}'s card in the participants list.",
  'users.chooseMain.none': 'No main character',
  'library.menu.visible': 'Make visible to everyone',
  'library.menu.invisible': 'Make invisible',
  'library.hidden': 'Hidden from players',
  'library.hiddenByFolder': 'Hidden from players by its folder',
  'library.notVisible': 'This item is not visible to you.',
  'library.menu.setOwner': 'Change the sheet owner…',
  'library.setOwner.prompt': 'Who can edit "{sheet}", besides the GMs.',
  'library.owner.me': '{name} (you)',
  'library.owner.none': 'Nobody: only the GMs edit it',
  'library.owner.of': "{name}'s",
  'library.owner.away': 'Someone who left the room',
  'library.newSheet.owner': 'Sheet owner',
  'library.newSheet.titleFor': 'New sheet for {name}',
  'users.card.title': '{name}\'s card',
  'users.card.hint': 'The sheet plugin also updates the bars by itself when the sheet is the main character.',
  'users.card.bar': 'Bar {n}',
  'users.card.value': '{name} value',
  'users.card.max': '{name} maximum',
  'users.card.line': 'Line {n}',
  'config.profile.avatar': 'Avatar',
  'config.profile.avatarHint': 'Shown in contacts, on your profile and on your card at tables.',
  'config.profile.avatarPick': 'Choose image…',
  'config.profile.avatarRemove': 'Remove avatar',
  'config.profile.avatarSaved': 'Avatar changed.',
  'config.profile.avatarInvalid': 'Couldn\'t use that image.',
  'config.profile.cover': 'Cover',
  'config.profile.coverPick': 'Choose cover…',
  'config.profile.coverHint': 'The image at the top of your profile: from your Desk Cloud, your computer or a web address.',
  'library.exportXml': 'Export as XML…',
  'library.importXml': 'Import from XML…',
  'library.importXml.confirm': 'Replace the content of "{name}" with {file}? What\'s in the sheet now will be replaced.',
  'library.importXml.go': 'Import',
  'library.importXml.done': 'Sheet "{name}" imported.',
  'library.importXml.failed': 'Couldn\'t import: {message}',
  'library.dock.enable': 'Enable table: {type}',
  'library.dock.disable': 'Disable table: {type}',
  'library.dock.disable.confirm': 'The "{type}" table leaves the room for everyone. Its data is kept: enable it again to bring it back as it was.',
  'library.dock.disable.go': 'Disable',
  'scene.tool.draw': 'Draw (D)',
  'scene.tool.erase': 'Erase drawings (E)',
  'scene.tool.align': 'Align the grid with the image',
  'scene.align.title': 'Align the grid with the map image',
  'scene.align.width': 'Width',
  'scene.align.height': 'Height',
  'scene.align.widthValue': 'Image width in cells',
  'scene.align.heightValue': 'Image height in cells',
  'scene.align.narrower': 'Make the image narrower',
  'scene.align.wider': 'Make the image wider',
  'scene.align.shorter': 'Make the image shorter',
  'scene.align.taller': 'Make the image taller',
  'scene.align.move.left': 'Move the image left',
  'scene.align.move.right': 'Move the image right',
  'scene.align.move.up': 'Move the image up',
  'scene.align.move.down': 'Move the image down',
  'scene.align.marks': 'Marked cells: {n}',
  'scene.align.angle': 'Rotation',
  'scene.align.angleValue': 'Image rotation in degrees',
  'scene.align.turnLeft': 'Rotate the image counterclockwise',
  'scene.align.turnRight': 'Rotate the image clockwise',
  'scene.align.legacy': 'This scene predates automatic fitting: the image is at its natural size and the grid may not match the map.',
  'scene.align.legacyFix': 'Fit the image to the grid',
  'scene.align.sync': 'Synchronize',
  'scene.align.clearMarks': 'Clear marks',
  'scene.align.detect': 'Detect the image grid',
  'scene.align.fitBoard': 'Fit the board to the image',
  'scene.align.hint': 'Drag over cells drawn on the map to mark them (four, spread out, work best) and click Synchronize. Arrows and buttons fine-tune (rotation in 0.05° steps); hold Shift for 10× steps.',
  'scene.align.detected': 'Found the image grid: the map is {cols} × {rows} cells.',
  'scene.align.rounded': 'The image was fitted to {cols} × {rows} cells. If the map has a drawn grid, use Align to match it.',
  'scene.align.synced': 'Grid synchronized with the marked cells.',
  'scene.align.redetected': 'Found the image grid and aligned it.',
  'scene.align.none': "Couldn't find a drawn grid in this image: it was fitted to {cols} × {rows} whole cells. To match it, mark a few cells and click Synchronize.",
  'scene.draw.options': 'Drawing options',
  'scene.draw.free': 'Freehand',
  'scene.draw.rect': 'Rectangle',
  'scene.draw.ellipse': 'Ellipse',
  'scene.draw.text': 'Text',
  'scene.draw.textPrompt': 'Type the text',
  'scene.draw.color': 'Color {color}',
  'scene.draw.width.s': 'Thin',
  'scene.draw.width.m': 'Medium',
  'scene.draw.width.l': 'Thick',
  'scene.draw.fill': 'Fill',
  'scene.draw.eraseHint': 'Click a drawing to erase it.',
  'scene.draw.clear': 'Clear…',
  'scene.draw.clear.mine': 'Clear my drawings',
  'scene.draw.clear.players': 'Clear players\' drawings',
  'scene.draw.clear.all': 'Clear all drawings',
  'scene.draw.clear.confirm': '{n} drawing(s) will be erased for everyone.',
  'scene.draw.clear.go': 'Erase',
  'combat.title': 'Combat tracker',
  'combat.loading': 'Opening the tracker…',
  'combat.writeFailed': 'Could not save: {message}',
  'combat.round': 'Round',
  'combat.add': 'Add actor',
  'combat.name': 'Name',
  'combat.actor.default': 'Actor',
  'combat.addPlayers': 'Add all players',
  'combat.addPlayers.short': '+ Players',
  'combat.noPlayers': 'No new players to add.',
  'combat.rollAll': 'Roll initiative for everyone (with a roll)',
  'combat.rollAll.short': 'Roll',
  'combat.noRolls': 'Nobody has an initiative roll. Open an actor and fill in "Roll".',
  'combat.rollLabel': 'Initiative of {name}',
  'combat.rollFailed': '{name}\'s roll failed: {message}',
  'combat.turnOf': '{name}\'s turn',
  'combat.effectEnded': '{name}\'s {effect} effect has ended',
  'combat.clear': 'Clear',
  'combat.clear.confirm': 'Remove all actors and go back to round 1?',
  'combat.next': 'Next turn',
  'combat.prev': 'Previous turn',
  'combat.turnBack': 'Back to {name}\'s turn',
  'combat.empty.gm': 'Add actors with + or bring the players with "+ Players".',
  'combat.empty': 'The GM has not set up the combat yet.',
  'combat.init': '{name}\'s initiative',
  'combat.side.none': 'Friendly or hostile?',
  'combat.side.friend': 'Friendly',
  'combat.side.neutral': 'Neutral',
  'combat.side.foe': 'Hostile',
  'combat.vis.public': 'Visible to everyone',
  'combat.vis.protected': 'Visible to everyone, without notes and effects',
  'combat.vis.private': 'Visible to GMs only',
  'combat.remove': 'Remove actor',
  'combat.remove.confirm': 'Remove {name} from the combat?',
  'combat.rollExpr': 'Initiative roll',
  'combat.notes': 'Notes',
  'combat.effect.add': '+ Effect',
  'combat.effect.name': 'Effect or condition',
  'combat.effect.rounds': 'Duration (rounds)',
  'combat.effect.left': '{n} round(s) left',
  'combat.effect.remove': 'Remove the {name} effect',
  'combat.nothing': 'No notes or effects.',
  'combat.invisible': 'Invisible: only those who see the token see this actor',
  'combat.link': 'Linked to a token in scene {scene}',
  'combat.link.gone': '(deleted scene)',
  'combat.unlink': 'Unlink from token',
  'combat.protected': 'The GM did not share the details.',
  'macro.manager': 'Manage macros',
  'macro.new': 'New macro',
  'macro.newName': 'macro',
  'macro.delete': 'Delete macro',
  'macro.delete.confirm': 'Delete the /{name} macro?',
  'macro.tab.room': 'Room (for everyone)',
  'macro.tab.mine': 'Mine in this room',
  'macro.tab.global': 'Mine (global)',
  'macro.hint.room': 'Everyone in the room can use these; only GMs change them.',
  'macro.hint.mine': 'Only you use them, only in this room. Kept in this browser.',
  'macro.hint.global': 'Only you use them, in every room. Kept in this browser.',
  'macro.empty': 'No macros here yet.',
  'macro.name': 'Macro name',
  'macro.name.invalid': 'Use a name without spaces or "/" (up to 30 characters).',
  'macro.name.taken': 'There is already a macro with this name here.',
  'macro.lines': 'Lines of the /{name} macro',
  'macro.lines.placeholder': 'One line per command, as in the chat:\n/r 1d20+5\n/me attacks with the sword',
  'macro.saveFailed': 'Could not save the macro: {message}',
  'macro.tooDeep': 'The /{name} macro calls too many macros in a row; stopped here.',
  'macro.tooLong': 'The /{name} macro went past {max} lines in one run; stopped here.',
  'macro.emptyRun': 'The /{name} macro is empty.',
  'cmd.macros.title': '/macros',
  'cmd.macros.desc': 'Opens the macro manager (each macro becomes a /name command).',
  'room.modules': 'Room modules',
  'imagePicker.title': 'Choose image',
  'imagePicker.hint': 'Click to choose the image',
  'imagePicker.tab.upload': 'From computer',
  'imagePicker.tab.room': 'Room images',
  'imagePicker.tab.web': 'Web address',
  'imagePicker.choose': 'Choose image file…',
  'imagePicker.uploading': 'Uploading…',
  'imagePicker.loading': 'Loading…',
  'imagePicker.noRoomImages': 'None of your images are in this room yet.',
  'imagePicker.url': 'Image address',
  'imagePicker.use': 'Use',
  'imagePicker.notImage': 'Choose an image file.',
  'imagePicker.tab.cloud': 'Desk Cloud',
  'imagePicker.search': 'Search the Desk Cloud',
  'imagePicker.noCloudImages': 'No images in your Desk Cloud yet.',
  'imagePicker.noMatch': 'No image with that name.',
  'imagePicker.more': 'Show more',
  'imagePicker.noFolderImages': 'No images in this folder.',
  'imagePicker.images': 'Images',
  'imagePicker.allImages': 'All images',
  'imagePicker.tooBig': 'Larger than {max}',
  'imagePicker.badType': 'Use PNG, JPEG, WebP or GIF',
  'imagePicker.uploadHint': 'The image goes to your Desk Cloud, reduced if it is large.',
  'imagePicker.import': 'Import',
  'imagePicker.importing': 'Importing…',
  'imagePicker.importHint': 'A copy of the image goes to your Desk Cloud, reduced if it is large.',
  'imagePicker.webFailed': 'Could not download an image from that address.',
  'dice.rng.mark': 'The table\'s dice use true random numbers, from random.org',
  'dice.rng.mark.fallback': 'random.org wasn\'t available for the table\'s last roll: the numbers came from the program',
  'config.general.diceEffects': 'Show special dice effects',
  'config.dice.effects': 'Special effects',
  'config.dice.effects.hint': 'They go with your rolls: everyone at the table sees your dice effects.',
  'config.dice.effects.none': 'No effects: your dice roll without special effects.',
  'config.dice.effects.die': 'Die',
  'config.dice.effects.face': 'Result',
  'config.dice.effects.effect': 'Effect',
  'config.dice.effects.max': 'Highest ({n})',
  'config.dice.effects.min': 'Lowest (1)',
  'config.dice.effects.preview': 'Test',
  'config.dice.effects.remove': 'Remove effect',
  'config.dice.effects.add': 'Add effect',
  'config.dice.effects.default': 'Restore default',
  'config.dice.effects.stage': 'Click "Test" to throw a test die with the effect.',
  'config.dice.effects.no3d': "This browser can't show 3D: see the notice at the top of this page.",
  'config.dice.effects.failed': '3D failed in this browser. Rolls still show in the chat; reloading the page usually fixes it.',
  'dice.no3d.title': "3D dice don't show up in this browser",
  'dice.no3d.why.gpuOff': "The browser's graphics acceleration is off, and without it no site can draw 3D. Rolls still show in the chat. To turn it on:",
  'dice.no3d.why.unknown': "The browser isn't drawing 3D: usually its graphics acceleration is off or blocked. Rolls still show in the chat. To turn it on:",
  'dice.no3d.why.policy': "3D was blocked by a rule on this computer (from a company or school, for example) or by an option used to start the browser. Turning on graphics acceleration won't fix it: talk to whoever manages the computer, or use another browser. Rolls still show in the chat.",
  'dice.no3d.step.open': 'Open the browser settings from its menu.',
  'dice.no3d.step.openKeys': 'Open the browser settings ({keys}).',
  'dice.no3d.step.searchAcceleration': 'Search the settings for “acceleration”.',
  'dice.no3d.step.searchPerformance': 'Search the settings for “performance”.',
  'dice.no3d.step.turnOn': 'Turn on {option} and relaunch the browser.',
  'dice.no3d.step.uncheck': 'Uncheck {option}.',
  'dice.no3d.step.check': 'Check {option} and restart the browser.',
  'dice.no3d.step.quit': 'If nothing changes, close every browser window and open it again.',
  'dice.no3d.step.quitKeys': 'If nothing changes, quit the browser completely ({keys}) and open it again.',
  'dice.no3d.step.safari': 'Safari has 3D on by default. Update macOS, or open RPG Desk in Chrome, Opera or Edge.',
  'dice.no3d.option.graphics': 'Use graphics acceleration when available',
  'dice.no3d.option.firefoxRecommended': 'Use recommended performance settings',
  'dice.no3d.option.firefoxHardware': 'Use hardware acceleration when available',
  'dice.no3d.details': 'Browser message',
  'dice.no3d.help': 'See the help page',
  'dice.no3d.helpAnchor': 'the-3d-dice-do-not-show-up',
  'dice.no3d.toast.gpuOff': "3D dice don't show up: this browser's graphics acceleration is off.",
  'dice.no3d.toast.unknown': "3D dice don't show up: this browser isn't drawing 3D.",
  'dice.no3d.toast.policy': "3D dice don't show up: 3D is blocked in this browser.",
  'dice.no3d.toast.fix': 'How to turn it on',
  'dice.no3d.toast.why': 'See why',
  'dice.effect.fire': 'Fire',
  'dice.effect.ice': 'Ice',
  'dice.effect.acid': 'Acid',
  'dice.effect.stone': 'Stone',
  'dice.effect.tavern': 'Tavern',
  'dice.effect.casino': 'Rich Akeime',
  'dice.effect.nature': 'Nature',
  'dice.effect.divine': 'Divine light',
  'dice.effect.lightning': 'Lightning',
  'config.dice.body': 'Dice color',
  'config.dice.number': 'Number color',
  'scene.undo': 'Undo (Ctrl+Z)',
  'scene.redo': 'Redo (Ctrl+Y)',
  'scene.undo.stale': 'Could not undo: the scene changed since then.',
  'scene.token.copied': '"{name}" copied. Ctrl+V pastes under the mouse.',
  'scene.token.duplicate': 'Duplicate (Ctrl+D)',
  'settings.tab.game': 'Game rules',
  'settings.tab.plugins': 'Plugins',
  'settings.game.none': 'None',
  'settings.game.bars': 'Card bars',
  'settings.game.bars.hint': 'The bars (health, mana…) shown on each player\'s card. Sheet plugins fill in the values.',
  'settings.game.bars.count': 'How many bars',
  'settings.game.bar.color': 'Color of bar {n}',
  'settings.game.bar.color.reset': 'Use the plugin\'s color',
  'settings.game.bar.name': 'Name of bar {n}',
  'settings.game.bar.numbers': 'Who sees the numbers of bar {n}',
  'settings.game.bar.kind': 'How to show the numbers of bar {n}',
  'settings.game.numbers.everyone': 'Numbers: everyone',
  'settings.game.numbers.gmOwner': 'Numbers: GMs and owner',
  'settings.game.kind.values': 'As value / max',
  'settings.game.kind.percent': 'As percentage',
  'settings.game.lines': 'Card lines',
  'settings.game.lines.hint': 'Free text lines on each player\'s card (condition, position…). The name shows when editing the card.',
  'settings.game.lines.count': 'How many lines',
  'settings.game.line.name': 'Name of line {n}',
  'settings.game.dice': 'Dice',
  'settings.game.dice.animation': 'Animate the 3D dice',
  'settings.game.dice.animation.hint': 'The dice roll on everyone\'s screen, and the result shows in the chat when they stop. When off, nobody at the table sees the animation, and the result shows right away.',
  'settings.game.dice.source': 'Dice numbers',
  'settings.game.dice.program': 'Generated by the program',
  'settings.game.dice.randomOrg': 'True random (random.org)',
  'settings.game.dice.hint': 'Applies to every roll at the table. With random.org, the numbers come from atmospheric noise, and an R shows next to the chat tab. If random.org isn\'t available, the roll comes from the program and the R is struck through.',
  'settings.game.sessions': 'Sessions',
  'settings.game.sessions.hint': 'The session clock counts play time and keeps each session in the Sessions tab.',
  'settings.game.sessions.record': 'Record sessions',
  'settings.game.sessions.record.hint': 'The clock, the lines in the chat and the Sessions tab. When off, none of it shows, and earlier sessions stay saved.',
  'settings.game.sessions.chatClock': 'Clock in the chat',
  'settings.game.sessions.chatClock.hint': "The play time in the chat's tab strip (on a phone, under the room name). Without it, the GM runs the session from the Sessions tab.",
  'settings.game.sessions.barClock': 'Clock on the room button',
  'settings.game.sessions.barClock.hint': "The ring around the room's initials, in the left bar.",
  'settings.game.sessions.ending': 'Session {n} is in progress: saving ends it.',
  'users.card.nothing': 'This room\'s game rules show no bars or lines.',
  'users.card.barChange': '{player}\'s {bar}: {delta}',
  'users.card.maxDelta': 'max',
  'library.dock.erase': 'Erase stored data: {type}',
  'library.dock.erase.confirm': 'The stored data of the "{type}" table will be erased for good.',
  'library.dock.erase.go': 'Erase',
  'macro.panel': 'Macros',
  'macro.manage': 'Manage…',
  'macro.panel.empty': 'No macros yet. Create one in "Manage…" or with /macros in the chat.',
  'scene.bg.pick': 'Map image',
  'scene.bg.failed': 'Couldn\'t use that image as the map: {message}',
  'room.popout': 'Open in new window',
  'room.popout.back': 'Back to the table',
  'room.popout.blocked': 'The browser blocked the new window. Allow pop-up windows for this site and try again.',
  'room.popout.login': 'Sign in to RPG Desk from the main window and open this window again.',
  'scene.tool.walls': 'Walls (W)',
  'scene.walls.options': 'Wall options',
  'scene.walls.mode.draw': 'Draw',
  'scene.walls.mode.select': 'Select',
  'scene.walls.mode.cut': 'Remove with rectangle',
  'scene.walls.shape.line': 'Segments',
  'scene.walls.shape.rect': 'Rectangle',
  'scene.walls.shape.ellipse': 'Ellipse',
  'scene.walls.shape.free': 'Freehand',
  'scene.walls.snap': 'Snap to grid',
  'scene.walls.join': 'Join nearby ends',
  'scene.walls.join.hint': 'Moves to the same point the ends that are less than a quarter of a cell from another wall: of the selected walls, or of all of them.',
  'scene.walls.join.done': 'Ends joined: {n}.',
  'scene.walls.join.none': 'No end is close enough to another wall to join.',
  'scene.walls.clear': 'Delete all walls',
  'scene.walls.clear.confirm': 'Delete the {n} walls of this scene? Ctrl+Z undoes it.',
  'scene.walls.deleteSelected': 'Delete selected ({n})',
  'scene.walls.hint.line': 'Click to place points; near another wall\'s point, it snaps to it. Double-click or Enter finishes; clicking the first point closes; Backspace removes the last one; Esc cancels.',
  'scene.walls.hint.shape': 'Drag to draw the shape.',
  'scene.walls.hint.select': 'Click a wall (Shift adds) or drag a box. Drag a vertex to move it (near another wall\'s point, it snaps). Delete removes.',
  'scene.walls.hint.cut': 'Drag a rectangle: whatever is inside it is cut out of the walls.',
  'scene.viewAs': 'View the scene as',
  'scene.viewAs.gm': 'Game master',
  'scene.viewAs.all': 'All players',
  'scene.vision': 'Token vision',
  'scene.vision.enabled': 'Token vision (players see what their tokens see; walls block sight)',
  'scene.vision.sharing': 'Sharing',
  'scene.vision.sharing.individual': 'Individual vision',
  'scene.vision.sharing.group': 'Shared vision',
  'scene.token.vision': 'Vision',
  'scene.token.hasSight': 'Has vision (NPC: counts in the game master\'s "View as"; a player\'s token always gives them vision)',
  'scene.token.sightBright': 'Range in bright light ({unit})',
  'scene.token.visionGmOnly': 'Only the game master changes the token\'s vision and light.',
  'scene.walls.preset.wall': 'Wall',
  'scene.walls.preset.window': 'Window',
  'scene.walls.preset.ethereal': 'Ethereal',
  'scene.walls.preset.terrain': 'Terrain',
  'scene.walls.preset.door': 'Door',
  'scene.walls.preset.secret': 'Secret door',
  'scene.walls.properties': 'Wall properties',
  'scene.walls.propertiesButton': 'Properties…',
  'scene.walls.prop.many': 'Applies to the {n} selected walls.',
  'scene.walls.prop.type': 'Type',
  'scene.walls.prop.custom': 'Custom',
  'scene.walls.prop.move': 'Blocks movement',
  'scene.walls.prop.sight': 'Sight',
  'scene.walls.prop.sight.normal': 'Blocks',
  'scene.walls.prop.sight.limited': 'Limited (terrain)',
  'scene.walls.prop.sight.none': 'Does not block (window)',
  'scene.walls.prop.dir': 'Direction',
  'scene.walls.prop.dir.both': 'Both sides',
  'scene.walls.prop.dir.left': 'Only for whoever is on the left',
  'scene.walls.prop.dir.right': 'Only for whoever is on the right',
  'scene.walls.prop.door': 'Door',
  'scene.walls.prop.door.none': 'Not a door',
  'scene.walls.prop.door.door': 'Door',
  'scene.walls.prop.door.secret': 'Secret door',
  'scene.walls.prop.state': 'State',
  'scene.walls.prop.state.closed': 'Closed',
  'scene.walls.prop.state.open': 'Open',
  'scene.walls.prop.state.locked': 'Locked',
  'scene.walls.prop.hint.terrain': 'Terrain: sight passes the first limited wall and stops at the second. Outline the whole object (the rock, the pillar): whoever looks sees the object, but not what is behind it.',
  'scene.walls.prop.hint.terrainOpen': 'Open line: sight crosses this wall only once and goes on. To hide what is behind it, close the outline (when drawing, click the first point) or add another limited wall beyond it.',
  'scene.walls.prop.hint.dir': 'Left and right count going from the first point to the second. The wall only counts for whoever looks from the chosen side; from the other side, it is not there.',
  'scene.walls.prop.doorTwoPoints': 'Only one-segment walls (2 points) can be doors.',
  'scene.door.open': 'Open the door',
  'scene.door.close': 'Close the door',
  'scene.door.locked': 'Locked door',
  'scene.door.locked.gm': 'Locked door: right-click to unlock',
  'scene.door.isLocked': 'The door is locked.',
  'scene.ping.spectator': 'Spectators cannot ping the map. Ask the GM to make you a player.',
  'scene.door.lockedNotice': '{name} tried to open a locked door',
  'scene.token.addToCombat': 'Add to combat',
  'scene.token.addToCombat.hint': 'Puts the token in the combat tracker, linked to it: while invisible, the token leaves combat for whoever does not see it',
  'scene.token.inCombat': 'Already in combat',
  'scene.token.addedToCombat': '{name} joined combat',
  'scene.token.conditions': 'Conditions',
  'scene.token.conditionsHint': 'Everyone at the table sees the icons on the token. Who still sees an invisible token is chosen in the token menu, under Conditions › Invisible….',
  'scene.cond.blind': 'Blinded',
  'scene.cond.dead': 'Dead',
  'scene.cond.poisoned': 'Poisoned',
  'scene.cond.stunned': 'Stunned',
  'scene.cond.paralyzed': 'Paralyzed',
  'scene.cond.prone': 'Prone',
  'scene.cond.frightened': 'Frightened',
  'scene.cond.unconscious': 'Unconscious',
  'scene.cond.bleeding': 'Bleeding',
  'scene.token.invisible': 'Invisible…',
  'scene.token.invisible.on': 'Invisible (on)…',
  'scene.token.invisible.hint': 'Only the GM, whoever controls the token and the people you choose see it',
  'scene.invisible.title': 'Invisible: {name}',
  'scene.invisible.on': 'Make the token invisible',
  'scene.invisible.viewers': 'Who still sees it',
  'scene.invisible.always': 'The GM and whoever controls the token always see it. Anyone not checked sees nothing of it: image, name and bar.',
  'scene.invisible.nobody': 'There is no one else at the table now.',
  'scene.invisible.away': 'Someone away from the table',
  'scene.invisible.badge': 'Invisible',
  'scene.invisible.notice.on': '{name} made {token} invisible',
  'scene.invisible.notice.viewers': '{name} changed who sees {token}',
  'scene.invisible.notice.off': '{name} made {token} visible again',
  'scene.tool.semi': 'Partially visible area',
  'scene.tool.semi.hint': 'Shown dimmed.',
  'scene.tool.explore': 'Area to explore',
  'scene.tool.explore.hint': 'Back to the default: hidden until seen.',
  'scene.vision.resetMemory': 'Reset exploration',
  'scene.vision.resetMemory.confirm': 'Erase what every token has already seen? The map goes back to hidden wherever nobody is looking now.',
  'scene.token.sightDim': 'Range in dim light ({unit})',
  'scene.token.sightDark': 'Darkvision ({unit})',
  'scene.token.lightBright': 'Bright light it emits ({unit})',
  'scene.token.lightDim': 'Dim light it emits ({unit})',
  'scene.token.lightOnly': 'Light only (players do not see the token, only its light)',
  'scene.token.rangesHint': 'A range of 0 means not seeing in that light. Default: 102 in bright light, 1.5 in dim light and 0 darkvision. Darkvision sees with no light at all, the map and the tokens, and what it shows appears colorless and darker.',
  'scene.aura': 'Aura',
  'scene.aura.has': 'Has an aura',
  'scene.aura.shape': 'Shape',
  'scene.aura.shape.circle': 'Circle',
  'scene.aura.shape.cone': 'Cone',
  'scene.aura.shape.semicircle': 'Half circle',
  'scene.aura.shape.rect': 'Rectangle',
  'scene.aura.shape.image': 'Image',
  'scene.aura.reach': 'Reach ({unit})',
  'scene.aura.reachHint': "Counted from the token's edge: a 3 m aura covers everything within 3 m of it, and grows with the token. The cone and the half circle measure their length ahead.",
  'scene.aura.angle': 'Opening (degrees)',
  'scene.aura.width': 'Width ({unit})',
  'scene.aura.height': 'Height ({unit})',
  'scene.aura.color': 'Aura color',
  'scene.aura.opacity': 'Opacity',
  'scene.aura.grid': 'In squares (paints the grid cells)',
  'scene.aura.image': 'Choose image…',
  'scene.aura.image.pick': 'Aura image',
  'scene.aura.image.missing': 'Choose the aura image.',
  'scene.aura.hint': "The aura moves and turns with the token: the cone and the half circle point where it faces (Turn 45° on the right-click menu, or Shift + wheel). Whoever sees the token sees the aura.",
  'scene.vision.ambient': 'Ambient light',
  'scene.vision.ambient.intense': 'Bright / Sunlight',
  'scene.vision.ambient.weak': 'Dim / Twilight',
  'scene.vision.ambient.none': 'None / Darkness',
  'scene.token.turnLeft': 'Turn 45° left',
  'scene.token.turnRight': 'Turn 45° right',
  'scene.token.turnHint': 'Shift + wheel turns 15° at a time',
  'scene.token.angle': 'Vision angle (degrees)',
  'scene.token.lightAngle': 'Light angle (degrees)',
  'scene.token.facingMode': 'Facing',
  'scene.token.facingMode.rotate': 'Rotate the image',
  'scene.token.facingMode.arrow': 'Show an arrow',
  'scene.uvtt.import': 'Import UVTT map…',
  'scene.uvtt.light': 'Light',
  'scene.uvtt.done': 'Map imported: {walls} walls, {doors} doors and {lights} lights. Fog and token vision were turned on.',
  'scene.uvtt.failed': 'Could not import the map: {message}',
  'scene.uvtt.tooBig': 'The map file is larger than {size}.',
  'scene.uvtt.imageTooBig': "The map image is larger than {size}, your plan's limit.",
  'scene.tool.select.hint': 'Click a token to select it; drag it to move it. Dragging the empty map moves the view. Alt + click (or holding still) pings for everyone.',
  'scene.tool.pan.hint': 'Only moves the view: the drag goes over tokens without selecting or moving any. With any tool, the middle or right mouse button also drags the view.',
  'webPlugin.loading.sheet': 'Opening the sheet…',
  'webPlugin.loading.table': 'Opening the table…',
  'webPlugin.timeout': 'The page did not answer. Does it load rpgdesk.js?',
  'webPlugin.navigated': 'The page tried to go somewhere else and was closed.',
  'webPlugin.hung': 'The page stopped responding.',
  'webPlugin.reload': 'Reload',
  'webPlugin.close': 'Close',
  'webPlugin.reopen': 'Open again',
  'webPlugin.noEntry': 'The plugin has no page for this sheet.',
  'webPlugin.dev': 'Development: {url}',
  'webPlugin.errors.title': 'Page errors',
  'webPlugin.errors.copy': 'Copy errors',
  'webPlugin.errors.clear': 'Clear',
  'webPlugin.errors.copied': 'Errors copied. Paste them in the conversation with the AI or with the plugin author.',
  'webPlugin.link.title': 'Open site',
  'webPlugin.link.message': 'The plugin "{plugin}" wants to open {url} in a new tab.',
  'webPlugin.link.go': 'Open',
  'webPlugin.aiGuide': 'Copy instructions for AI',
  'webPlugin.aiGuide.hint': 'Copies the guide to making sheets and tables for RPG Desk. Paste it at the start of the conversation with the AI and describe your sheet.',
  'webPlugin.aiGuide.copied': 'Instructions copied. Paste them at the start of the conversation with the AI and describe the sheet you want.',
  'webPlugin.hooks': 'Integrations ({count})',
  'webPlugin.hooks.hint': 'The plugin can send data to the webhooks below. Paste the webhook address (Zapier, Make or IFTTT). Addresses stay on the server; players do not see them.',
  'webPlugin.hooks.url': 'Address of webhook {name}',
  'webPlugin.hooks.who': 'Who can trigger {name}',
  'webPlugin.hooks.gm': 'GMs only',
  'webPlugin.hooks.everyone': 'Everyone',
  'webPlugin.hooks.save': 'Save',
  'webPlugin.hooks.remove': 'Remove',
  'webPlugin.hooks.on': 'On.',
  'webPlugin.hooks.off': 'Off: no address.',
  'webPlugin.hooks.log': 'Latest sends',
  'webPlugin.devmode': 'Development',
  'webPlugin.devmode.hint': 'Loads the sheet or table from an address on your computer (for example, your project dev server). Only in this browser; everyone else at the table keeps the installed version.',
  'webPlugin.devmode.url': 'Development address of {title}',
  'webPlugin.devmode.off': 'Turn off',
  'webInstall.title': 'Install plugin',
  'webInstall.issues': 'Problems found',
  'webInstall.error': 'Error',
  'webInstall.warning': 'Warning',
  'webInstall.fix': 'How to fix: {fix}',
  'webInstall.copy': 'Copy report',
  'webInstall.copied': 'Report copied. Paste it in the conversation with the AI so it can fix it.',
  'webInstall.installing': 'Installing…',
  'webInstall.install': 'Install',
  'webInstall.update': 'Update',
  'webInstall.by': 'by {author}',
  'webInstall.updating': 'Updates the installed version {from} to {to}. Sheets keep their data.',
  'webInstall.downgrade': 'Version {to} is lower than the installed {from}.',
  'webInstall.sheet': 'Sheet: {title}',
  'webInstall.table': 'Room table: {title}',
  'webInstall.webhooks': 'It can send data outside, through the webhooks you register later in Integrations:',
  'webInstall.unreadable': 'Could not read the plugin in "{file}".',
  'webInstall.hasErrors': 'With errors the plugin is not installed. Copy the report and take it to the AI or to the plugin author.',
  'webInstall.cloudFailed': 'Could not download "{file}" from the Desk Cloud.',
  'webInstall.noRoom': 'Join a room before installing.',
  'webInstall.failed': 'The server did not install the plugin.',
  'pluginInstall.tabs': 'Where the plugin comes from',
  'pluginInstall.tab.official': 'Official',
  'pluginInstall.tab.cloud': 'Desk Cloud',
  'pluginInstall.tab.computer': 'Computer',
  'pluginInstall.official.forSystem': 'For {system}',
  'pluginInstall.official.others': 'Others',
  'pluginInstall.official.none': 'No official plugins yet',
  'pluginInstall.official.noneText': 'Install a plugin from your Desk Cloud or from your computer.',
  'pluginInstall.official.failed': 'Could not load the official plugins: {message}',
  'pluginInstall.retry': 'Try again',
  'pluginInstall.installed': 'Installed',
  'pluginInstall.installLabel': 'Install {name}',
  'pluginInstall.updateTo': 'Update to {version}',
  'pluginInstall.updateLabel': 'Update {name} to version {version}',
  'pluginInstall.updateTitle': 'Update {name} to version {version}',
  'pluginInstall.kind.sheet': 'Sheet',
  'pluginInstall.kind.sheets': '{n} sheets',
  'pluginInstall.kind.table': 'Room table',
  'pluginInstall.kind.tables': '{n} room tables',
  'pluginInstall.cloud.empty': 'No plugin packages (.zip or .html) in your Desk Cloud.',
  'pluginInstall.cloud.package': 'Package',
  'pluginInstall.cloud.hint': 'Only .zip and .html packages show up. A .zip installs directly, with no new copy in the Desk Cloud.',
  'pluginInstall.cloud.continue': 'Continue',
  'pluginInstall.computer.drop': 'Drop the plugin .zip or .html here',
  'pluginInstall.computer.pick': 'Choose the plugin .zip or .html',
  'pluginInstall.computer.choose': 'Choose file…',
  'pluginInstall.computer.hint': 'RPG Desk checks the package before installing. It stays in your Desk Cloud, linked to the room.',
  'pluginInstall.reading': 'Checking…',
  'webPlugin.errors.button': 'Page errors ({count})',
  'webPlugin.errors.reportSheet': 'Errors of the sheet "{name}" (plugin {plugin})',
  'webPlugin.errors.reportTable': 'Errors of the table "{name}" (plugin {plugin})',

  // área de administração (docs/spec/admin.md)
  'logged.nav.admin': 'Administration',
  'signedOut.suspended.title': 'Account suspended',
  'signedOut.suspended.text': 'An administrator suspended your account. While it is suspended, you cannot sign in.',
  'signedOut.password.title': 'Password changed',
  'signedOut.password.text': 'An administrator changed your password. Sign in with the new password they gave you.',
  'signedOut.deleted.title': 'Account deleted',
  'signedOut.deleted.text': 'An administrator deleted your account.',
  'signedOut.reason': 'Reason: {reason}',
  'admin.title': 'Administration',
  'admin.section.overview': 'Overview',
  'admin.section.users': 'Users',
  'admin.section.plans': 'Plans',
  'admin.section.rooms': 'Rooms',
  'admin.section.plugins': 'Official plugins',
  'admin.section.server': 'Server',
  'admin.section.log': 'Log',
  'admin.overview.accounts': 'Accounts',
  'admin.overview.total': 'All accounts',
  'admin.overview.newLastWeek': 'new this week: {n}',
  'admin.overview.online': 'Online now',
  'admin.overview.suspended': 'Suspended',
  'admin.overview.admins': 'Administrators',
  'admin.overview.plans': 'Accounts per plan',
  'admin.overview.server': 'Server',
  'admin.overview.space': 'Space used',
  'admin.overview.files': 'files: {n}',
  'admin.overview.rooms': 'Rooms',
  'admin.overview.activeRooms': 'with people now: {n}',
  'admin.overview.uploads': 'Uploads being read',
  'admin.overview.uploadsLimit': 'ceiling of {limit}',
  'admin.rooms.search': 'Search by room or owner',
  'admin.rooms.sort': 'Order',
  'admin.rooms.sort.recent': 'Newest',
  'admin.rooms.sort.name': 'Name',
  'admin.rooms.sort.members': 'Most members',
  'admin.rooms.sort.space': 'Most space',
  'admin.rooms.count': 'Rooms found: {n}',
  'admin.rooms.none': 'No room matches this search.',
  'admin.rooms.col.room': 'Room',
  'admin.rooms.col.owner': 'Owner',
  'admin.rooms.col.members': 'Members',
  'admin.rooms.col.online': 'Now',
  'admin.rooms.col.files': 'Files',
  'admin.rooms.col.created': 'Created',
  'admin.rooms.col.last': 'Last message',
  'admin.rooms.filesValue': '{n} · {size}',
  'admin.rooms.private': 'Private',
  'admin.rooms.password': 'Password',
  'admin.rooms.never': 'never',
  'admin.rooms.more': 'Actions for {name}',
  'admin.rooms.openOwner': 'See the owner',
  'admin.rooms.delete': 'Delete room',
  'admin.rooms.deleteTitle': 'Delete the room {name}',
  'admin.rooms.deleteLabel': 'Type the room name ({name}) to confirm. The room, its sheets, scene, chat and files go away for good, and whoever is in it leaves right away.',
  'admin.rooms.deleteDone': 'The room {name} was deleted.',
  'admin.rooms.deleteMismatch': "The name you typed is not the room's.",
  'admin.roomDelete.detail': 'Room: {name}',
  'admin.server.hint': 'Everything here changes in apps/server/.env (see .env.example), with the server restarted.',
  'admin.server.running': 'Running',
  'admin.server.version': 'RPG Desk version',
  'admin.server.node': 'Node',
  'admin.server.startedAt': 'Running since',
  'admin.server.limits': 'Limits',
  'admin.server.maxUpload': 'Ceiling for one file',
  'admin.server.uploadMemory': 'Upload memory',
  'admin.server.inUseOf': '{used} in use, of {limit}',
  'admin.server.maxBody': 'Largest JSON request',
  'admin.server.history': 'History when joining a room',
  'admin.server.messages': '{n} messages',
  'admin.server.access': 'Access and services',
  'admin.server.admins': 'Administrators in the configuration',
  'admin.server.cors': 'Addresses of the separately hosted interface',
  'admin.server.plugins': 'Plugins address',
  'admin.server.samePlace': "this server's (/p)",
  'admin.server.storage': 'Bucket folder',
  'admin.server.randomOrg': 'random.org dice',
  'admin.server.client': 'Serves the interface',
  'admin.server.on': 'Yes',
  'admin.server.off': 'No',
  'admin.server.none': 'none',
  'admin.tab.users': 'Users',
  'admin.tab.log': 'Log',
  'admin.users.search': 'Search by name or e-mail',
  'admin.users.filter': 'Show',
  'admin.users.filter.all': 'All',
  'admin.users.filter.online': 'Online',
  'admin.users.filter.admins': 'Administrators',
  'admin.users.filter.suspended': 'Suspended',
  'admin.users.sort': 'Order',
  'admin.users.sort.recent': 'Newest',
  'admin.users.sort.name': 'Name',
  'admin.users.sort.seen': 'Last seen',
  'admin.users.plan': 'Plan',
  'admin.users.plan.all': 'All plans',
  'admin.users.plan.paid': 'Paid plans',
  'admin.users.count': 'Accounts found: {n}',
  'admin.users.none': 'No account matches this search.',
  'admin.col.account': 'Account',
  'admin.col.email': 'E-mail',
  'admin.col.created': 'Created',
  'admin.col.seen': 'Last seen',
  'admin.col.rooms': 'Rooms',
  'admin.col.space': 'Space',
  'admin.col.plan': 'Plan',
  'admin.more': 'Actions for {name}',
  'admin.details': 'View details',
  'admin.loadMore': 'Load more',
  'admin.online': 'Online now',
  'admin.neverSeen': 'Never connected',
  'admin.badge.admin': 'Admin',
  'admin.badge.admin.env': 'Administrator by the server configuration (RPGDESK_ADMINS)',
  'admin.badge.admin.db': 'Promoted to administrator on screen',
  'admin.badge.suspended': 'Suspended',
  'admin.action.suspend': 'Suspend',
  'admin.action.unsuspend': 'Reactivate',
  'admin.action.password': 'Temporary password',
  'admin.action.promote': 'Make admin',
  'admin.action.demote': 'Remove admin',
  'admin.action.delete': 'Delete account',
  'admin.action.plan': 'Change plan',
  'admin.refusal.self': 'Not for your own account',
  'admin.refusal.configured': 'Administrator by the server configuration: change it there',
  'admin.refusal.admin': 'Remove the administrator role first',
  'admin.refusal.suspended': 'Reactivate the account first',
  'admin.suspend.title': 'Suspend {name}',
  'admin.suspend.label': 'Reason (shown when they try to sign in). Their open sessions end right away.',
  'admin.suspend.done': '{name} was suspended.',
  'admin.unsuspend.confirm': '{name} will be able to sign in again.',
  'admin.unsuspend.done': '{name} was reactivated.',
  'admin.password.title': 'Temporary password for {name}',
  'admin.password.label': 'Copy the password to give to them (or type another). Their open sessions end, and they sign in with this one.',
  'admin.password.action': 'Change password',
  'admin.password.done': "{name}'s password was changed.",
  'admin.password.doneCopied': "{name}'s password was changed and copied.",
  'admin.promote.confirm': '{name} will see the administration area and be able to act on other accounts.',
  'admin.promote.done': '{name} is now an administrator.',
  'admin.demote.confirm': '{name} will no longer see the administration area.',
  'admin.demote.done': '{name} is no longer an administrator.',
  'admin.delete.title': 'Delete {name}',
  'admin.delete.label': 'The account, its rooms, files and messages are gone for good. To confirm, type the account name: {name}',
  'admin.delete.mismatch': "The name typed is not the account's.",
  'admin.delete.done': 'The account {name} was deleted.',
  'admin.plan.title': "Change {name}'s plan",
  'admin.plan.plan': 'Plan',
  'admin.plan.noExpiry': 'No end date',
  'admin.plan.validUntil': 'Valid until',
  'admin.plan.note': 'Internal note (only administrators see it)',
  'admin.plan.freeNoExpiry': 'The Free plan has no end date.',
  'admin.plan.same': 'The account is already on this plan, with this end date.',
  'admin.plan.overUse': "The account uses {used}, and {plan} gives {space}. It can't upload new files until it frees space; nothing is deleted.",
  'admin.plan.done': "{name}'s plan: {plan}.",
  'admin.plan.until': 'until {date}',
  'admin.plan.expiredOn': '{plan} ended on {date}',
  'admin.plan.noExpiryShort': 'no end date',
  'admin.plan.grantedBy': 'given by {name}',
  'admin.log.empty': 'No administrator actions yet.',
  'admin.log.col.when': 'When',
  'admin.log.col.admin': 'Who',
  'admin.log.col.action': 'Action',
  'admin.log.col.target': 'Account',
  'admin.log.col.detail': 'Details',
  'admin.log.suspend': 'Suspended',
  'admin.log.unsuspend': 'Reactivated',
  'admin.log.password': 'Changed the password',
  'admin.log.delete': 'Deleted the account',
  'admin.log.promote': 'Made admin',
  'admin.log.demote': 'Removed admin',
  'admin.log.plan': 'Changed the plan',
  'admin.log.plan-config': 'Edited the plan',
  'admin.log.room-delete': 'Deleted the room',
  'admin.log.plugin-add': 'Published the plugin',
  'admin.log.plugin-version': 'Published a new version',
  'admin.log.plugin-systems': 'Changed the systems',
  'admin.log.plugin-hide': 'Hid from the catalog',
  'admin.log.plugin-show': 'Put back in the catalog',
  'admin.log.plugin-delete': 'Deleted the plugin',
  'admin.planConfig.reset': 'Back to the default',
  'admin.planConfig.name.pt': 'Name (pt)',
  'admin.planConfig.name.en': 'Name (en)',
  'admin.planConfig.price': 'Price',
  'admin.plans.hint': "What you save holds right away for every account on the plan. Nothing is deleted: whoever goes over the space just can't upload new files.",
  'admin.plans.readOnly': "Only the administrators in the server's configuration (RPGDESK_ADMINS) change the plans. Here you can see them, without editing.",
  'admin.plugins.hint': 'Game masters install these plugins from the Official tab of "Install plugin…". A new version does not change the rooms: each game master updates when they want.',
  'admin.plugins.readOnly': "Only the administrators in the server's configuration (RPGDESK_ADMINS) change the official plugins. Here you can see them, without editing.",
  'admin.plugins.add': 'Add plugin…',
  'admin.plugins.none': 'No official plugins',
  'admin.plugins.noneText': 'The plugins added here show up for every game master in the Official tab of "Install plugin…".',
  'admin.plugins.count': 'Plugins: {n}',
  'admin.plugins.col.plugin': 'Plugin',
  'admin.plugins.col.version': 'Version',
  'admin.plugins.col.contents': 'Contents',
  'admin.plugins.col.systems': 'Systems',
  'admin.plugins.col.usage': 'Rooms',
  'admin.plugins.col.updated': 'Published',
  'admin.plugins.roomsOutdated': '{n} · {old} on an old version',
  'admin.plugins.updatedBy': 'Published by {name}',
  'admin.plugins.noSystems': 'None',
  'admin.plugins.hiddenTag': 'Hidden',
  'admin.plugins.more': 'Actions for {name}',
  'admin.plugins.newVersion': 'Send new version…',
  'admin.plugins.editSystems': 'Systems…',
  'admin.plugins.hide': 'Hide from the catalog',
  'admin.plugins.hideHint': 'Rooms that already have the plugin keep it',
  'admin.plugins.show': 'Show in the catalog',
  'admin.plugins.hiddenDone': '{name} left the catalog.',
  'admin.plugins.shownDone': '{name} is back in the catalog.',
  'admin.plugins.delete': 'Delete',
  'admin.plugins.deleteInUse': 'Installed in rooms ({n}): hide it instead of deleting',
  'admin.plugins.deleteTitle': 'Delete the plugin {name}',
  'admin.plugins.deleteText': 'The plugin leaves the catalog with every published version. No room uses it now.',
  'admin.plugins.deleteDone': '{name} was deleted.',
  'admin.plugins.addTitle': 'Add official plugin',
  'admin.plugins.newVersionTitle': 'New version of {name}',
  'admin.plugins.computerHint': 'The package is checked before publishing and stays on the server, outside your Desk Cloud.',
  'admin.plugins.cloudHint': 'Only .zip and .html packages show up. Publishing makes a copy on the server: the file in your Desk Cloud can go later.',
  'admin.plugins.isNew': 'New plugin in the catalog.',
  'admin.plugins.versionUp': 'New version of {name}: {from} → {to}. Rooms stay on their version until the game master updates.',
  'admin.plugins.versionChange': '{from} → {to}. Rooms stay on their version until the game master updates.',
  'admin.plugins.sameVersion': 'Version {version} was already published. Raise the version in manifest.json.',
  'admin.plugins.older': 'The published version is {version}. Send a higher version.',
  'admin.plugins.otherPlugin': 'This package belongs to another plugin: the new version needs the id {id}.',
  'admin.plugins.systemsLabel': 'Systems',
  'admin.plugins.systemsHint': 'In rooms of these systems, the plugin shows up at the top of the Official tab.',
  'admin.plugins.systemPlaceholder': 'Type a system and press Enter',
  'admin.plugins.systemsFull': 'Limit of systems reached',
  'admin.plugins.addSystem': 'Add',
  'admin.plugins.removeSystem': 'Remove {system}',
  'admin.plugins.roomSystems': 'In rooms:',
  'admin.plugins.addKnown': 'Add {system} (rooms: {n})',
  'admin.plugins.visibleNow': 'Show in the catalog now',
  'admin.plugins.publish': 'Publish',
  'admin.plugins.addDone': '{name} is in the catalog.',
  'admin.plugins.versionDone': '{name} {version} published.',
  'admin.plugins.systemsTitle': 'Systems of {name}',
  'admin.plugins.systemsDone': 'The systems of {name} changed.',
  'admin.plugins.save': 'Save',
  'admin.plugins.logSystems': 'Systems: {systems}',
  'admin.plugins.logNoSystems': 'No systems',
  'admin.plans.price': 'Price per month',
  'admin.plans.accounts': 'Accounts on the plan',
  'admin.plans.overSpace': 'Over the space',
  'admin.plans.cut': '{value} (the server cuts it to {holds})',
  'admin.plans.edit': 'Edit',
  'admin.plans.reset': 'Back to the default',
  'admin.plans.resetConfirm': "{plan} goes back to the code's space, largest file, name and price. It holds right away for every account on it.",
  'admin.plans.resetDone': '{plan} is back to the default.',
  'admin.plans.edited': 'Edited',
  'admin.plans.editedBy': 'Edited by {name}',
  'admin.plans.editTitle': 'Edit {plan}',
  'admin.plans.save': 'Save',
  'admin.plans.saved': '{plan} was saved.',
  'admin.plans.namePt': 'Name in Portuguese',
  'admin.plans.nameEn': 'Name in English',
  'admin.plans.priceLabel': 'Price per month (R$)',
  'admin.plans.freeNoPrice': 'The Free plan has no price',
  'admin.plans.unit': 'Unit of {what}',
  'admin.plans.range': 'Use a value between {min} and {max}.',
  'admin.plans.overCeiling': "Above the server's ceiling: {ceiling} holds until the ceiling goes up (RPGDESK_MAX_UPLOAD_BYTES).",
  'admin.plans.badPrice': 'Invalid price: use reais, with up to two cents digits (7 or 7.50).',
  'admin.plans.nesting': '{what}: {plan} would have less than {below}. A plan cannot give less than the plan below it.',
  'admin.plans.impact': "Accounts on this plan that go over the new space: {n}. They can't upload new files until they free space; nothing is deleted.",
  'admin.plans.unchanged': 'Nothing changed.',
  'admin.log.gone': 'The account no longer exists',
  'admin.panel.label': 'Details of {name}',
  'admin.panel.close': 'Close the details',
  'admin.panel.suspendedBy': 'Suspended by {name} on {date}',
  'admin.panel.reason': 'Reason: {reason}',
  'admin.panel.noReason': 'No reason given.',
  'admin.panel.created': 'Account created',
  'admin.panel.lastLogin': 'Last sign-in',
  'admin.panel.sessions': 'Open sessions',
  'admin.panel.tos': 'Terms of use',
  'admin.panel.tosAccepted': 'Version {version}, accepted on {date}',
  'admin.panel.tosNone': 'Not accepted',
  'admin.panel.space': '{used} of {quota} · files: {files}',
  'admin.panel.friends': 'Friends',
  'admin.panel.rooms': 'Rooms created ({n})',
  'admin.panel.noRooms': 'No rooms.',
  'admin.panel.history': 'Administration history',
  'admin.panel.noHistory': 'No administrator actions on this account.',

  /* plans (docs/spec/planos.md) */
  'plans.name.free': 'Free',
  'plans.name.tier1': 'Plan 1',
  'plans.name.tier2': 'Plan 2',
  'plans.name.tier3': 'Plan 3',
  'plans.entitlement.storageBytes': 'Space',
  'plans.entitlement.maxFileBytes': 'Largest file',
  'plans.short.storageBytes': '{value}',
  'plans.short.maxFileBytes': 'files up to {value}',
  'plans.unlimited': 'unlimited',
  'plans.yes': 'Yes',
  'plans.no': 'No',
  'plans.from': 'From {plan}',
  'plans.full': 'Space full: delete files or change your plan',
  'plans.noPrice': 'no price',
  'plans.upload.tooLarge': "The file is over {size}, your plan's limit.",
  'plans.upload.noSpace': "It doesn't fit in your space: delete files or change your plan.",
  'landing.nav': 'Site navigation',
  'landing.nav.credits': 'Credits',
  'landing.nav.plans': 'Plans',
  'landing.nav.docs': 'Documentation',
  'landing.beta': 'Beta',
  'landing.beta.hint': 'RPG Desk is a project in development.',
  'landing.signIn': 'Sign in',
  'landing.signUp.short': 'Sign up',
  'landing.newTab': '(opens in a new tab)',
  'landing.back': 'About RPG Desk',
  'landing.hero.title': 'Your group plays here. Every week.',
  'landing.hero.text': 'A map with fog and per-token light, 3D dice that land for everyone, character sheets, voice and the campaign journal. In the browser, nothing to install, free.',
  'landing.hero.gm': 'Create my table — free',
  'landing.hero.player': 'I want to play: find a table',
  'landing.live.label': 'Sample session: The Crypt of Valdren',
  'landing.live.camera.gm': 'GM\'s camera',
  'landing.live.camera.player': 'Aria\'s camera',
  'landing.live.sample': 'Sample session',
  'landing.live.show': 'The Crypt of Valdren · session 12',
  'landing.live.clock': 'Your time',
  'landing.live.viewAs': 'View as',
  'landing.live.viewAs.gm': 'GM',
  'landing.live.viewAs.player': 'Aria (player)',
  'landing.live.fogHint': 'Drag Aria: the fog lifts wherever she goes.',
  'landing.live.token': 'Aria\'s token. Use the arrow keys to move.',
  'landing.live.skeleton': 'Skeleton',
  'landing.live.table': 'At the table',
  'landing.live.people': '{count} people',
  'landing.live.speaking': 'Speaking',
  'landing.live.role.gm': 'GM',
  'landing.live.role.spectator': 'Spectator',
  'landing.live.now': 'Now',
  'landing.live.events': 'Marina: The torches flicker. Something moves behind the columns…\nAria rolled 1d20+5 = 17 and hit the skeleton\nBram: I hold the door. Nobody gets through.\nTeo joined the table through the link\nMarina started the Combat track\nAria sent the GM a private message',
  'landing.tray.title': 'Your roll',
  'landing.tray.note': 'The table\'s own physics and effects',
  'landing.tray.effect': 'Effect',
  'landing.tray.noEffect': 'No effect',
  'landing.tray.roll': 'Roll 1d20',
  'landing.tray.rolling': 'Rolling…',
  'landing.tray.you': 'You · 1d20',
  'landing.tray.sample1': 'Aria · 1d20+5 · attack',
  'landing.tray.sample2': 'Skeleton · 1d6 · damage',
  'landing.tray.sample3': 'Bram · 1d20+3 · attack',
  'landing.tray.no3d': 'Without graphics acceleration the die shows still, with the same draw.',
  'landing.tray.failed': 'The 3D dice failed in this browser. The draw still counts.',
  'landing.tray.result': 'Rolled {value}',
  'landing.seg.zero.time': 'Session 0',
  'landing.seg.zero.label': 'Sheets',
  'landing.seg.zero.title': 'Your system\'s sheet, ready-made or made by you.',
  'landing.seg.zero.text': 'In session zero, the group creates the characters. In RPG Desk, a sheet is a web page: install an official one in one click, ask an AI for one or write your own in HTML, even without knowing how to code.',
  'landing.seg.zero.items': 'Official — D&D 5e, the Basic Sheet and RPG Desk\'s other sheets: in Room settings → Plugins → Install plugin…, Official tab, with the ones for the table\'s system at the top\nWith AI — The Copy instructions for AI button gets you the guide; describe your system\'s sheet and install the file the AI gives back\nIn HTML — A field is a data-field, a roll is a data-roll; JavaScript only if you want\nTwo languages — The same sheet in Portuguese and English, with the translation next to the text, no code to switch\nPer table — Each table installs the sheets and tables it uses, from Official, Desk Cloud or your computer, and the GM updates whenever they want\nLive — The sheet saves as you type, and the table sees it at once\nGM-only parts — What is just for the GM never even reaches the players\' browsers',
  'landing.seg.zero.dnd': 'D&D 5e',
  'landing.seg.zero.credits': 'D&D 5e is an official RPG Desk sheet, in both languages and with the SRD 5.1 rules (CC BY 4.0): at the table, it\'s in the Official tab.',
  'landing.seg.zero.open': 'See the D&D 5e sheet live first, in the docs',
  'landing.seg.zero.make': 'Make your own',
  'landing.seg.zero.makeText': 'These lines are a working sheet. Change the name or Strength and roll.',
  'landing.seg.zero.code': 'sheet.html',
  'landing.seg.zero.result': 'The sheet, live',
  'landing.seg.zero.demo.name': 'Name',
  'landing.seg.zero.demo.nameField': 'name',
  'landing.seg.zero.demo.strength': 'Strength',
  'landing.seg.zero.demo.strengthField': 'strength',
  'landing.seg.zero.demo.roll': 'Roll Strength',
  'landing.seg.zero.demo.label': '{name}\'s Strength check',
  'landing.seg.zero.demo.sample': 'Aria',
  'landing.seg.zero.demo.empty': 'The roll shows up here, as in the table chat.',
  'landing.seg.prep.time': '7:40 pm',
  'landing.seg.prep.label': 'Prep',
  'landing.seg.prep.title': 'The GM sets the scene before anyone arrives.',
  'landing.seg.prep.text': 'The map with its grid, tokens, walls and doors, the fog on top, and the campaign files at hand in Desk Cloud.',
  'landing.seg.prep.items': 'Grid and tokens — The grid detected on the map itself, and tokens dragged from your computer\nFog of war — The GM reveals areas with the mouse\nVision and light — Per token, with darkvision and the area already explored\nWalls and doors — Windows and doors, even locked and secret ones\nUVTT maps — From Dungeondraft, they arrive with walls, doors and lights\nDesk Cloud — "Use at the table" brings a file over in one click, and "Who can open" keeps spoilers away',
  'landing.seg.prep.camera': 'GM\'s camera · Walls tool',
  'landing.seg.prep.pip': 'Desk Cloud',
  'landing.seg.arrive.time': '8:00 pm',
  'landing.seg.arrive.label': 'The group arrives',
  'landing.seg.arrive.title': 'The group arrives by link. Voice is already at the table.',
  'landing.seg.arrive.text': 'Whoever has the invite goes straight to the table, on a computer or a phone. Whoever doesn\'t have a group yet looks for a table in Rooms.',
  'landing.seg.arrive.items': 'Invite — By link, with a password and a welcome message if the GM wants\nVoice and video — Right at the table, with each person\'s camera on their card\nRooms — The public tables, filtered by system, language, frequency and visuals\nPhone — The table becomes tabs: Chat, Scene, Sheets, People and More\nFriends — Status and direct messages on any screen',
  'landing.seg.arrive.cta': 'Create an account to play',
  'landing.seg.arrive.events': 'Luana joined through the link\nDavi turned on the mic\nTeo joined as a spectator\nMarina opened the scene',
  'landing.seg.arrive.camera': 'Rooms · find a table',
  'landing.seg.arrive.phone': 'On a phone',
  'landing.seg.combat.time': '8:30 pm',
  'landing.seg.combat.label': 'Combat',
  'landing.seg.combat.title': 'The dice land on everyone\'s screen at the same time.',
  'landing.seg.combat.text': 'The roll happens on the server, and nobody picks the result. The sheet rolls from its buttons, the tracker announces each turn in the chat and the track plays for everyone.',
  'landing.seg.combat.items': 'Rolls — /r 1d20+5, quick dice from d4 to d100, successes and Fudge\n3D dice — In your colors, with effects like Fire, Ice and Lightning\nSheets — They save as you type, with GM-only parts\nTracker — Initiative, turns and effects with a duration\nMusic — Tracks and sound effects playing for everyone at the same time\nMacros — Your commands in one word, like /attack',
  'landing.seg.combat.music': 'Playing for everyone: Combat',
  'landing.seg.combat.camera': '3D dice over the table',
  'landing.seg.combat.sheet': 'Aria\'s sheet',
  'landing.seg.diary.time': '11:00 pm',
  'landing.seg.diary.label': 'Journal',
  'landing.seg.diary.title': 'The clock stops, and the journal keeps the night.',
  'landing.seg.diary.text': 'The session clock tracks everyone\'s time of play, and each session is recorded with a title, notes and who was there. Next week, the table is still there, just as you left it.',
  'landing.seg.diary.items': 'Clock — The time of play for everyone, with pauses\nRecord — Each session with a title, notes and who was there\nHome — Takes you back to your tables, right where you stopped\nThe table stays — Sheets, scenes and files stay at the table and in Desk Cloud',
  'landing.seg.diary.board': 'Session ended',
  'landing.seg.diary.played': '{time} of play',
  'landing.seg.diary.ended': 'Session ended at 11:12 pm',
  'landing.seg.diary.next': 'Next session: next week, same time',
  'landing.docsLinks': 'In the documentation',
  'landing.credits.title': 'Credits',
  'landing.credits.text': 'Everything that comes with the table. Nothing hidden: each feature has its own page in the documentation.',
  'landing.alt.ficha-dnd5e': 'The D&D 5e sheet of Aria Folhaverde, a level 3 half-elf ranger: the name on the banner, class, background and race, the six abilities with their modifiers and skills, AC 15, 9 m speed and 24 of 28 hit points.',
  'landing.alt.combate-tracker': "The combat tracker: Next turn, the players, roll and clear buttons, and the initiative with Aria's turn (17), the Skeleton (14, hostile) and Bram (12).",
  'landing.alt.cena-paredes': "The Walls tool: the options bar with Draw, Select, Remove with rectangle, the wall types and shapes, and the hall's walls in red with a yellow door on the east wall.",
  'landing.alt.arquivos': "Desk Cloud: on the left the views, the rooms, the folders and the space per type; in the middle the folders and the files as cards, with each one's type and rooms; on the right the details of a map, with Use in a room and Who can open.",
  'landing.alt.explorar': 'Explore on the Playing now list: the filter column on the left and, on the right, room cards with a cover, the system, who is at the table and badges such as Looking for players.',
  'landing.alt.celular-inicio': 'Home on a phone: the last room highlighted, with its cover on top, the Create room and Explore buttons, the strip with the open room and the bottom bar with Home, Rooms, Friends, Files and More.',
  'landing.alt.dados-3d': 'The 3D dice of the latest rolls tumbling over the room, across the library and the chat.',
  'landing.alt.ficha': "Aria's sheet open in a tab of the room: portrait, name, origin, class, health and energy, defense, speed and the attribute list with the roll buttons.",
  'landing.alt.map': 'The crypt map seen from above: the hall with four columns and lit torches, a room with an altar, a storeroom with crates and the sarcophagus chamber, with the tokens of Aria and Bram and a skeleton.',
  'landing.alt.sessoes': 'The Sessions tab: the session in play, with the clock, the pause and end buttons and who is in the session; below, the earlier sessions, The warden at the door and The descent into the crypt, with the day and the time of play.',
  'landing.alt.voz-conferencia': 'The call panel with two people, Marina and Luana, and the microphone, camera and leave buttons.',
  'landing.features.group.table': 'The table',
  'landing.features.group.scene': 'Scene and map',
  'landing.features.group.sheets': 'Sheets and plugins',
  'landing.features.group.gm': 'For game masters',
  'landing.features.group.group': 'The group',
  'landing.features.group.where': 'Files and where to play',
  'landing.feature.layout.title': 'Tabs and spaces',
  'landing.feature.layout.text': 'Arrange the room your way: drag, split and join the tabs.',
  'landing.feature.chat.title': 'Chat',
  'landing.feature.chat.text': 'Narration, NPC lines, actions, formatting, emoji, mentions and attachments.',
  'landing.feature.dice.title': 'Rolls',
  'landing.feature.dice.text': 'Expressions, quick dice, successes, Fudge and 3D dice for everyone.',
  'landing.feature.library.title': 'Library',
  'landing.feature.library.text': 'Sheets, scenes and folders that start hidden until the game master releases them.',
  'landing.feature.participants.title': 'Participants',
  'landing.feature.participants.text': 'Game masters, players and spectators, with a photo and bars on each card.',
  'landing.feature.macros.title': 'Macros',
  'landing.feature.macros.text': 'Your own commands in one word, and a table of buttons.',
  'landing.feature.scene.title': 'Building a scene',
  'landing.feature.scene.text': 'A map with a detected grid, a scale and tokens dragged from your computer.',
  'landing.feature.playerScene.title': "The player's scene",
  'landing.feature.playerScene.text': 'Moving with path and distance, a ruler, pings, drawings and doors.',
  'landing.feature.fog.title': 'Fog of war',
  'landing.feature.fog.text': 'Everything starts covered, and the game master reveals the areas.',
  'landing.feature.vision.title': 'Vision and light',
  'landing.feature.vision.text': 'Each player sees what their tokens see, and the game master has "View as".',
  'landing.feature.walls.title': 'Walls and doors',
  'landing.feature.walls.text': 'Walls, windows, terrain, locked doors and secret doors.',
  'landing.feature.uvtt.title': 'Importing maps',
  'landing.feature.uvtt.text': 'UVTT maps from Dungeondraft, with walls, doors and lights.',
  'landing.feature.tokens.title': 'Conditions and auras',
  'landing.feature.tokens.text': 'Condition icons, auras that turn with the token, and the invisible token.',
  'landing.feature.sheets.title': 'Live sheets',
  'landing.feature.sheets.text': 'They save as you type, and their buttons roll in the chat.',
  'landing.feature.gmParts.title': "Players' sheets",
  'landing.feature.gmParts.text': 'The game master creates and hands them out, and their parts never reach the players.',
  'landing.feature.create.title': 'Create your sheets',
  'landing.feature.create.text': 'A sheet is a web page: HTML, CSS and, if you want, JavaScript.',
  'landing.feature.ai.title': 'Sheets with AI',
  'landing.feature.ai.text': 'Copy the guide from the button on the Plugins page, describe the sheet and install the file.',
  'landing.feature.languages.title': 'Two languages',
  'landing.feature.languages.text': 'The same sheet in Portuguese and English, with the translation next to the text.',
  'landing.feature.examples.title': 'Ready-made examples',
  'landing.feature.examples.text': 'Sheets and tables to use or adapt, live and to download.',
  'landing.feature.tables.title': 'Room tables',
  'landing.feature.tables.text': 'Pages for the whole room, such as NPCs and a calendar, with hidden parts.',
  'landing.feature.plugins.title': 'Room plugins',
  'landing.feature.plugins.text': 'Official ones in one click, or a file from Desk Cloud or your computer; the checker explains what to fix.',
  'landing.feature.integrations.title': 'Integrations',
  'landing.feature.integrations.text': 'Webhooks to Discord, spreadsheets and lights, through Zapier, Make or IFTTT.',
  'landing.feature.combat.title': 'Combat tracker',
  'landing.feature.combat.text': 'Initiative, turns, rounds, sides and effects with a duration.',
  'landing.feature.clock.title': 'Session clock',
  'landing.feature.clock.text': 'Play time for everyone and a diary of every session.',
  'landing.feature.music.title': 'Music and effects',
  'landing.feature.music.text': 'A synced playlist and a sound effects board.',
  'landing.feature.roles.title': 'Roles and moderation',
  'landing.feature.roles.text': 'Silencing, a moderated chat, deleting messages, kicking and banning.',
  'landing.feature.rules.title': 'Game rules',
  'landing.feature.rules.text': 'Bars and lines on the cards, with the numbers for whoever you choose.',
  'landing.feature.roomSettings.title': 'Room settings',
  'landing.feature.roomSettings.text': 'Cover, logo, password, chat history and assistant game masters.',
  'landing.feature.voice.title': 'Voice and video',
  'landing.feature.voice.text': "A call in the room, with each person's webcam on their card.",
  'landing.feature.friends.title': 'Friends',
  'landing.feature.friends.text': 'Profile, status, direct messages and invitations to your room.',
  'landing.feature.explore.title': 'Explore',
  'landing.feature.explore.text': 'Public rooms, filters and the ones looking for players.',
  'landing.feature.invite.title': 'Create and invite',
  'landing.feature.invite.text': 'System, welcome message, password and the invitation link.',
  'landing.feature.windows.title': 'Windows of their own',
  'landing.feature.windows.text': 'Take tabs to another monitor, with up to 8 rooms open.',
  'landing.feature.home.title': 'Home',
  'landing.feature.home.text': 'Pick up where you left off, with your rooms and the most played systems.',
  'landing.feature.cloud.title': 'Desk Cloud',
  'landing.feature.cloud.text': 'Maps, tokens, audio and documents in folders, ready to use in a room.',
  'landing.feature.browser.title': 'In the browser',
  'landing.feature.browser.text': 'Chrome, Edge, Firefox or Opera, with nothing to install.',
  'landing.feature.mobile.title': 'On your phone',
  'landing.feature.mobile.text': 'Tabs made for your finger, and it installs as an app.',
  'landing.feature.desktop.title': 'Windows app',
  'landing.feature.desktop.text': 'The same table in an app for your computer.',
  'landing.plans.text': 'Rooms, sheets, scene, dice, voice and video, chat, plugins and friends are the same on every plan. Paid plans only add Desk Cloud space and larger files.',
  'landing.plans.notice': "Paid plans aren't available yet. For now, everyone plays on Free.",
  'landing.plans.available': 'Available',
  'landing.plans.soon': 'Coming soon',
  'landing.plans.more': 'How plans work',
  'landing.plans.title': 'Free. All of it.',
  'landing.end.title': 'Next week, same time. The table is yours.',
  'landing.end.text': 'Create your account, set up your first table and send the group the link.',
  'landing.end.signIn': 'I have an account: sign in',
  'landing.footer.help': 'Help',
};
